import React, { Suspense, lazy, useEffect, useState } from "react";
import { jsx } from "react/jsx-runtime";
import * as THREE from "three";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { GLTFLoader as GLTFLoader$1 } from "three/examples/jsm/loaders/GLTFLoader.js";
import pino from "pino";
import * as XLSX from "xlsx";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { parseAsString, parseAsStringEnum, useQueryState, useQueryStates } from "nuqs";
//#region src/lib/loadable.tsx
/**
* Оборачивает динамический import в React.lazy с Suspense.
*
* @example
* const Page = lazyLoad(() => import("./Page"));
* <Page title="Заявки" />
*
* @example
* const Chart = lazyLoad(
*   () => import("./module"),
*   (module) => module.Chart,
*   { fallback: <Spinner /> },
* );
*
* @param importFunc - функция dynamic import
* @param selectorFunc - выбор компонента из загруженного модуля; по умолчанию используется `default`
* @param opts - опции Suspense; по умолчанию `fallback: null`
* @returns компонент-обёртку с lazy loading
*/
const lazyLoad = (importFunc, selectorFunc, opts = { fallback: null }) => {
	let lazyFactory = importFunc;
	if (selectorFunc) lazyFactory = () => importFunc().then((module) => ({ default: selectorFunc(module) }));
	const LazyComponent = lazy(lazyFactory);
	return (props) => /* @__PURE__ */ jsx(Suspense, {
		fallback: opts.fallback,
		children: /* @__PURE__ */ jsx(LazyComponent, { ...props })
	});
};
//#endregion
//#region src/lib/uuidv4.ts
const hasNativeRandomUUID = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function";
let uuidV4Fn;
let uuidV4Import;
/** Loads the `uuid` package only when `crypto.randomUUID` is unavailable. */
function ensureUuidV4Polyfill() {
	if (uuidV4Fn) return Promise.resolve(uuidV4Fn);
	uuidV4Import ??= import("uuid").then(({ v4 }) => {
		uuidV4Fn = v4;
		return v4;
	});
	return uuidV4Import;
}
if (!hasNativeRandomUUID) ensureUuidV4Polyfill();
/** Uses `crypto.randomUUID` when available; otherwise the lazily loaded `uuid` package. */
function uuidv4() {
	if (hasNativeRandomUUID) return crypto.randomUUID();
	if (!uuidV4Fn) throw new Error("@synthesis/shared-utils uuidv4: polyfill is still loading; await ensureUuidV4Polyfill() first");
	return uuidV4Fn();
}
//#endregion
//#region src/lib/convertBlobToBase64.tsx
/**
* Конвертирует `Blob` в data URL (base64) через `FileReader`.
*
* @example
* const dataUrl = await convertBlobToBase64(new Blob(["hello"], { type: "text/plain" }));
* // "data:text/plain;base64,aGVsbG8="
*
* @param blob - blob для конвертации
* @returns promise с data URL или результатом `FileReader`
*/
const convertBlobToBase64 = (blob) => new Promise((resolve, reject) => {
	const reader = new FileReader();
	reader.onerror = reject;
	reader.onload = () => {
		resolve(reader.result);
	};
	reader.readAsDataURL(blob);
});
//#endregion
//#region src/lib/defaultMapboxStyle.ts
const defaultMapboxStyle = {
	version: 8,
	name: "Basic",
	metadata: {
		"mapbox:type": "default",
		"mapbox:origin": "basic-v1",
		"mapbox:sdk-support": {
			android: "9.3.0",
			ios: "5.10.0",
			js: "2.0.0"
		},
		"mapbox:autocomposite": true,
		"mapbox:groups": {
			"Transit, transit-labels": {
				name: "Transit, transit-labels",
				collapsed: false
			},
			"Administrative boundaries, admin": {
				name: "Administrative boundaries, admin",
				collapsed: false
			},
			"Road network, bridges": {
				name: "Road network, bridges",
				collapsed: false
			},
			"Land, water, & sky, water": {
				name: "Land, water, & sky, water",
				collapsed: false
			},
			"Road network, tunnels": {
				name: "Road network, tunnels",
				collapsed: false
			},
			"Road network, road-labels": {
				name: "Road network, road-labels",
				collapsed: false
			},
			"Buildings, built": {
				name: "Buildings, built",
				collapsed: false
			},
			"Natural features, natural-labels": {
				name: "Natural features, natural-labels",
				collapsed: false
			},
			"Road network, surface": {
				name: "Road network, surface",
				collapsed: false
			},
			"Land, water, & sky, built": {
				name: "Land, water, & sky, built",
				collapsed: false
			},
			"Walking, cycling, etc., barriers-bridges": {
				name: "Walking, cycling, etc., barriers-bridges",
				collapsed: false
			},
			"Place labels, place-labels": {
				name: "Place labels, place-labels",
				collapsed: false
			},
			"Buildings, extruded": {
				name: "Buildings, extruded",
				collapsed: false
			},
			"Point of interest labels, poi-labels": {
				name: "Point of interest labels, poi-labels",
				collapsed: false
			},
			"Walking, cycling, etc., tunnels": {
				name: "Walking, cycling, etc., tunnels",
				collapsed: false
			},
			"Walking, cycling, etc., walking-cycling-labels": {
				name: "Walking, cycling, etc., walking-cycling-labels",
				collapsed: false
			},
			"Walking, cycling, etc., surface": {
				name: "Walking, cycling, etc., surface",
				collapsed: false
			},
			"Transit, built": {
				name: "Transit, built",
				collapsed: false
			},
			"Land, water, & sky, land": {
				name: "Land, water, & sky, land",
				collapsed: false
			}
		},
		"mapbox:trackposition": true
	},
	center: [35.77299234319423, 58.172536869994104],
	zoom: 8.922021459399337,
	bearing: 32.799999999999955,
	pitch: 37,
	sources: { composite: {
		url: "",
		type: "vector"
	} },
	sprite: "",
	glyphs: "mapbox://fonts/mapbox/{fontstack}/{range}.pbf",
	layers: [
		{
			id: "land",
			type: "background",
			layout: { visibility: "none" },
			paint: { "background-color": "rgb(235, 243, 245)" },
			metadata: {
				"mapbox:featureComponent": "land-and-water",
				"mapbox:group": "Land, water, & sky, land"
			}
		},
		{
			minzoom: 5,
			layout: { visibility: "none" },
			metadata: {
				"mapbox:featureComponent": "land-and-water",
				"mapbox:group": "Land, water, & sky, land"
			},
			filter: [
				"==",
				["get", "class"],
				"national_park"
			],
			type: "fill",
			source: "composite",
			id: "national-park",
			paint: {
				"fill-color": "hsl(133, 38%, 100%)",
				"fill-opacity": [
					"interpolate",
					["linear"],
					["zoom"],
					5,
					0,
					6,
					.5,
					10,
					.5
				]
			},
			"source-layer": "landuse_overlay"
		},
		{
			minzoom: 5,
			layout: { visibility: "none" },
			metadata: {
				"mapbox:featureComponent": "land-and-water",
				"mapbox:group": "Land, water, & sky, land"
			},
			filter: [
				"match",
				["get", "class"],
				[
					"park",
					"airport",
					"glacier",
					"pitch",
					"sand",
					"facility"
				],
				true,
				"cemetery",
				true,
				"school",
				true,
				"hospital",
				true,
				false
			],
			type: "fill",
			source: "composite",
			id: "landuse",
			paint: {
				"fill-color": [
					"interpolate",
					["linear"],
					["zoom"],
					15,
					[
						"match",
						["get", "class"],
						"park",
						"hsl(133, 38%, 100%)",
						"airport",
						"hsl(135, 34%, 95%)",
						"cemetery",
						"hsl(115, 35%, 100%)",
						"glacier",
						"hsl(197, 23%, 100%)",
						"hospital",
						"hsl(200, 23%, 91%)",
						"pitch",
						"hsl(133, 39%, 95%)",
						"sand",
						"hsl(192, 37%, 93%)",
						"school",
						"hsl(148, 20%, 85%)",
						"rgb(219, 233, 237)"
					],
					16,
					[
						"match",
						["get", "class"],
						"park",
						"hsl(133, 38%, 100%)",
						"airport",
						"hsl(135, 48%, 93%)",
						"cemetery",
						"hsl(115, 35%, 100%)",
						"glacier",
						"hsl(197, 23%, 100%)",
						"hospital",
						"hsl(200, 11%, 93%)",
						"pitch",
						"hsl(133, 39%, 95%)",
						"sand",
						"hsl(192, 37%, 93%)",
						"school",
						"hsl(148, 20%, 85%)",
						"rgb(219, 233, 237)"
					]
				],
				"fill-opacity": [
					"interpolate",
					["linear"],
					["zoom"],
					5,
					0,
					6,
					[
						"match",
						["get", "class"],
						"glacier",
						.5,
						1
					]
				]
			},
			"source-layer": "landuse"
		},
		{
			minzoom: 15,
			layout: { visibility: "none" },
			metadata: {
				"mapbox:featureComponent": "land-and-water",
				"mapbox:group": "Land, water, & sky, land"
			},
			filter: [
				"==",
				["get", "class"],
				"pitch"
			],
			type: "line",
			source: "composite",
			id: "pitch-outline",
			paint: { "line-color": "hsl(115, 22%, 90%)" },
			"source-layer": "landuse"
		},
		{
			id: "waterway",
			type: "line",
			source: "composite",
			"source-layer": "waterway",
			minzoom: 8,
			layout: {
				"line-cap": [
					"step",
					["zoom"],
					"butt",
					11,
					"round"
				],
				"line-join": "round",
				visibility: "none"
			},
			paint: {
				"line-color": "hsl(197, 33%, 81%)",
				"line-width": [
					"interpolate",
					["exponential", 1.3],
					["zoom"],
					9,
					[
						"match",
						["get", "class"],
						["canal", "river"],
						.1,
						0
					],
					20,
					[
						"match",
						["get", "class"],
						["canal", "river"],
						8,
						3
					]
				],
				"line-opacity": [
					"interpolate",
					["linear"],
					["zoom"],
					8,
					0,
					8.5,
					1
				]
			},
			metadata: {
				"mapbox:featureComponent": "land-and-water",
				"mapbox:group": "Land, water, & sky, water"
			}
		},
		{
			id: "water",
			type: "fill",
			source: "composite",
			"source-layer": "water",
			layout: { visibility: "none" },
			paint: { "fill-color": "hsl(197, 33%, 81%)" },
			metadata: {
				"mapbox:featureComponent": "land-and-water",
				"mapbox:group": "Land, water, & sky, water"
			}
		},
		{
			minzoom: 13,
			layout: { visibility: "none" },
			metadata: {
				"mapbox:featureComponent": "land-and-water",
				"mapbox:group": "Land, water, & sky, built"
			},
			filter: [
				"all",
				[
					"==",
					["geometry-type"],
					"Polygon"
				],
				[
					"==",
					["get", "class"],
					"land"
				]
			],
			type: "fill",
			source: "composite",
			id: "land-structure-polygon",
			paint: { "fill-color": "rgb(235, 243, 245)" },
			"source-layer": "structure"
		},
		{
			minzoom: 13,
			layout: {
				"line-cap": "round",
				visibility: "none"
			},
			metadata: {
				"mapbox:featureComponent": "land-and-water",
				"mapbox:group": "Land, water, & sky, built"
			},
			filter: [
				"all",
				[
					"==",
					["geometry-type"],
					"LineString"
				],
				[
					"==",
					["get", "class"],
					"land"
				]
			],
			type: "line",
			source: "composite",
			id: "land-structure-line",
			paint: {
				"line-width": [
					"interpolate",
					["exponential", 1.99],
					["zoom"],
					14,
					.75,
					20,
					40
				],
				"line-color": "rgb(235, 243, 245)"
			},
			"source-layer": "structure"
		},
		{
			minzoom: 11,
			layout: { visibility: "none" },
			metadata: {
				"mapbox:featureComponent": "transit",
				"mapbox:group": "Transit, built"
			},
			filter: [
				"all",
				[
					"==",
					["geometry-type"],
					"Polygon"
				],
				[
					"match",
					["get", "type"],
					[
						"runway",
						"taxiway",
						"helipad"
					],
					true,
					false
				]
			],
			type: "fill",
			source: "composite",
			id: "aeroway-polygon",
			paint: {
				"fill-color": [
					"interpolate",
					["linear"],
					["zoom"],
					15,
					"hsl(135, 26%, 86%)",
					16,
					"hsl(135, 15%, 88%)"
				],
				"fill-opacity": [
					"interpolate",
					["linear"],
					["zoom"],
					11,
					0,
					11.5,
					1
				]
			},
			"source-layer": "aeroway"
		},
		{
			minzoom: 9,
			layout: { visibility: "none" },
			metadata: {
				"mapbox:featureComponent": "transit",
				"mapbox:group": "Transit, built"
			},
			filter: [
				"==",
				["geometry-type"],
				"LineString"
			],
			type: "line",
			source: "composite",
			id: "aeroway-line",
			paint: {
				"line-color": [
					"interpolate",
					["linear"],
					["zoom"],
					15,
					"hsl(135, 26%, 86%)",
					16,
					"hsl(135, 15%, 88%)"
				],
				"line-width": [
					"interpolate",
					["exponential", 1.5],
					["zoom"],
					9,
					[
						"match",
						["get", "type"],
						"runway",
						1,
						.5
					],
					18,
					[
						"match",
						["get", "type"],
						"runway",
						80,
						20
					]
				]
			},
			"source-layer": "aeroway"
		},
		{
			minzoom: 14,
			layout: { visibility: "none" },
			metadata: {
				"mapbox:featureComponent": "walking-cycling",
				"mapbox:group": "Walking, cycling, etc., tunnels"
			},
			filter: [
				"all",
				[
					"==",
					["get", "structure"],
					"tunnel"
				],
				[
					"==",
					["get", "class"],
					"path"
				],
				[
					"!=",
					["get", "type"],
					"steps"
				],
				[
					"==",
					["geometry-type"],
					"LineString"
				]
			],
			type: "line",
			source: "composite",
			id: "tunnel-path",
			paint: {
				"line-width": [
					"interpolate",
					["exponential", 1.5],
					["zoom"],
					15,
					1,
					18,
					4
				],
				"line-color": "rgb(202, 223, 229)",
				"line-dasharray": [
					"step",
					["zoom"],
					["literal", [1, 0]],
					15,
					["literal", [1.75, 1]],
					16,
					["literal", [1, .75]],
					17,
					["literal", [1, .5]]
				]
			},
			"source-layer": "road"
		},
		{
			minzoom: 14,
			layout: { visibility: "none" },
			metadata: {
				"mapbox:featureComponent": "walking-cycling",
				"mapbox:group": "Walking, cycling, etc., tunnels"
			},
			filter: [
				"all",
				[
					"==",
					["get", "structure"],
					"tunnel"
				],
				[
					"==",
					["get", "type"],
					"steps"
				],
				[
					"==",
					["geometry-type"],
					"LineString"
				]
			],
			type: "line",
			source: "composite",
			id: "tunnel-steps",
			paint: {
				"line-width": [
					"interpolate",
					["exponential", 1.5],
					["zoom"],
					15,
					1,
					16,
					1.6,
					18,
					6
				],
				"line-color": "rgb(202, 223, 229)",
				"line-dasharray": [
					"step",
					["zoom"],
					["literal", [1, 0]],
					15,
					["literal", [1.75, 1]],
					16,
					["literal", [1, .75]],
					17,
					["literal", [.3, .3]]
				]
			},
			"source-layer": "road"
		},
		{
			minzoom: 13,
			layout: { visibility: "none" },
			metadata: {
				"mapbox:featureComponent": "walking-cycling",
				"mapbox:group": "Walking, cycling, etc., tunnels"
			},
			filter: [
				"all",
				[
					"==",
					["get", "structure"],
					"tunnel"
				],
				[
					"==",
					["get", "class"],
					"pedestrian"
				],
				[
					"==",
					["geometry-type"],
					"LineString"
				]
			],
			type: "line",
			source: "composite",
			id: "tunnel-pedestrian",
			paint: {
				"line-width": [
					"interpolate",
					["exponential", 1.5],
					["zoom"],
					14,
					.5,
					18,
					12
				],
				"line-color": "rgb(202, 223, 229)",
				"line-dasharray": [
					"step",
					["zoom"],
					["literal", [1, 0]],
					15,
					["literal", [1.5, .4]],
					16,
					["literal", [1, .2]]
				]
			},
			"source-layer": "road"
		},
		{
			minzoom: 13,
			layout: { visibility: "none" },
			metadata: {
				"mapbox:featureComponent": "road-network",
				"mapbox:group": "Road network, tunnels"
			},
			filter: [
				"all",
				[
					"==",
					["get", "structure"],
					"tunnel"
				],
				[
					"step",
					["zoom"],
					[
						"match",
						["get", "class"],
						[
							"motorway",
							"motorway_link",
							"trunk",
							"trunk_link",
							"primary",
							"secondary",
							"tertiary",
							"street",
							"street_limited",
							"primary_link",
							"track"
						],
						true,
						false
					],
					14,
					[
						"match",
						["get", "class"],
						[
							"motorway",
							"motorway_link",
							"trunk",
							"trunk_link",
							"primary",
							"primary_link",
							"secondary",
							"secondary_link",
							"tertiary",
							"tertiary_link",
							"street",
							"street_limited",
							"service",
							"track"
						],
						true,
						false
					]
				],
				[
					"==",
					["geometry-type"],
					"LineString"
				]
			],
			type: "line",
			source: "composite",
			id: "tunnel-simple",
			paint: {
				"line-width": [
					"interpolate",
					["exponential", 1.5],
					["zoom"],
					13,
					[
						"match",
						["get", "class"],
						[
							"motorway",
							"trunk",
							"primary"
						],
						4,
						["secondary", "tertiary"],
						2.5,
						[
							"motorway_link",
							"trunk_link",
							"street",
							"street_limited",
							"primary_link"
						],
						1,
						.5
					],
					18,
					[
						"match",
						["get", "class"],
						[
							"motorway",
							"trunk",
							"primary"
						],
						32,
						["secondary", "tertiary"],
						26,
						[
							"motorway_link",
							"trunk_link",
							"street",
							"street_limited",
							"primary_link"
						],
						18,
						12
					]
				],
				"line-color": "hsl(171, 38%, 93%)"
			},
			"source-layer": "road"
		},
		{
			minzoom: 12,
			layout: {
				"line-join": [
					"step",
					["zoom"],
					"miter",
					14,
					"round"
				],
				visibility: "none"
			},
			metadata: {
				"mapbox:featureComponent": "walking-cycling",
				"mapbox:group": "Walking, cycling, etc., surface"
			},
			filter: [
				"all",
				[
					"==",
					["get", "class"],
					"path"
				],
				[
					"step",
					["zoom"],
					["!", [
						"match",
						["get", "type"],
						[
							"steps",
							"sidewalk",
							"crossing"
						],
						true,
						false
					]],
					16,
					[
						"!=",
						["get", "type"],
						"steps"
					]
				],
				[
					"match",
					["get", "structure"],
					["none", "ford"],
					true,
					false
				],
				[
					"==",
					["geometry-type"],
					"LineString"
				]
			],
			type: "line",
			source: "composite",
			id: "road-path",
			paint: {
				"line-width": [
					"interpolate",
					["exponential", 1.5],
					["zoom"],
					13,
					.5,
					14,
					1,
					15,
					1,
					18,
					4
				],
				"line-color": "rgb(255, 255, 255)",
				"line-dasharray": [
					"step",
					["zoom"],
					["literal", [4, .3]],
					15,
					["literal", [1.75, .3]],
					16,
					["literal", [1, .3]],
					17,
					["literal", [1, .25]]
				]
			},
			"source-layer": "road"
		},
		{
			minzoom: 14,
			layout: {
				"line-join": "round",
				visibility: "none"
			},
			metadata: {
				"mapbox:featureComponent": "walking-cycling",
				"mapbox:group": "Walking, cycling, etc., surface"
			},
			filter: [
				"all",
				[
					"==",
					["get", "type"],
					"steps"
				],
				[
					"match",
					["get", "structure"],
					["none", "ford"],
					true,
					false
				],
				[
					"==",
					["geometry-type"],
					"LineString"
				]
			],
			type: "line",
			source: "composite",
			id: "road-steps",
			paint: {
				"line-width": [
					"interpolate",
					["exponential", 1.5],
					["zoom"],
					15,
					1,
					16,
					1.6,
					18,
					6
				],
				"line-color": "rgb(255, 255, 255)",
				"line-dasharray": [
					"step",
					["zoom"],
					["literal", [1, 0]],
					15,
					["literal", [1.75, 1]],
					16,
					["literal", [1, .75]],
					17,
					["literal", [.3, .3]]
				]
			},
			"source-layer": "road"
		},
		{
			minzoom: 12,
			layout: {
				"line-join": [
					"step",
					["zoom"],
					"miter",
					14,
					"round"
				],
				visibility: "none"
			},
			metadata: {
				"mapbox:featureComponent": "walking-cycling",
				"mapbox:group": "Walking, cycling, etc., surface"
			},
			filter: [
				"all",
				[
					"==",
					["get", "class"],
					"pedestrian"
				],
				[
					"match",
					["get", "structure"],
					["none", "ford"],
					true,
					false
				],
				[
					"==",
					["geometry-type"],
					"LineString"
				]
			],
			type: "line",
			source: "composite",
			id: "road-pedestrian",
			paint: {
				"line-width": [
					"interpolate",
					["exponential", 1.5],
					["zoom"],
					14,
					.5,
					18,
					12
				],
				"line-color": "rgb(255, 255, 255)",
				"line-dasharray": [
					"step",
					["zoom"],
					["literal", [1, 0]],
					15,
					["literal", [1.5, .4]],
					16,
					["literal", [1, .2]]
				]
			},
			"source-layer": "road"
		},
		{
			minzoom: 5,
			layout: {
				"line-cap": [
					"step",
					["zoom"],
					"butt",
					14,
					"round"
				],
				"line-join": [
					"step",
					["zoom"],
					"miter",
					14,
					"round"
				],
				visibility: "none"
			},
			metadata: {
				"mapbox:featureComponent": "road-network",
				"mapbox:group": "Road network, surface"
			},
			filter: [
				"all",
				[
					"step",
					["zoom"],
					[
						"match",
						["get", "class"],
						["motorway", "trunk"],
						true,
						false
					],
					6,
					[
						"match",
						["get", "class"],
						[
							"motorway",
							"trunk",
							"primary"
						],
						true,
						false
					],
					8,
					[
						"match",
						["get", "class"],
						[
							"motorway",
							"trunk",
							"primary",
							"secondary"
						],
						true,
						false
					],
					10,
					[
						"match",
						["get", "class"],
						[
							"motorway",
							"trunk",
							"primary",
							"secondary",
							"tertiary",
							"motorway_link",
							"trunk_link"
						],
						true,
						false
					],
					11,
					[
						"match",
						["get", "class"],
						[
							"motorway",
							"motorway_link",
							"trunk",
							"trunk_link",
							"primary",
							"secondary",
							"tertiary",
							"street"
						],
						true,
						false
					],
					12,
					[
						"match",
						["get", "class"],
						[
							"motorway",
							"motorway_link",
							"trunk",
							"trunk_link",
							"primary",
							"secondary",
							"tertiary",
							"street",
							"street_limited",
							"primary_link"
						],
						true,
						false
					],
					13,
					[
						"match",
						["get", "class"],
						[
							"motorway",
							"motorway_link",
							"trunk",
							"trunk_link",
							"primary",
							"secondary",
							"tertiary",
							"street",
							"street_limited",
							"primary_link",
							"track"
						],
						true,
						false
					],
					14,
					[
						"match",
						["get", "class"],
						[
							"motorway",
							"motorway_link",
							"trunk",
							"trunk_link",
							"primary",
							"primary_link",
							"secondary",
							"secondary_link",
							"tertiary",
							"tertiary_link",
							"street",
							"street_limited",
							"service",
							"track"
						],
						true,
						false
					]
				],
				[
					"match",
					["get", "structure"],
					["none", "ford"],
					true,
					false
				],
				[
					"==",
					["geometry-type"],
					"LineString"
				]
			],
			type: "line",
			source: "composite",
			id: "road-simple",
			paint: {
				"line-width": [
					"interpolate",
					["exponential", 1.5],
					["zoom"],
					5,
					[
						"match",
						["get", "class"],
						[
							"motorway",
							"trunk",
							"primary"
						],
						.75,
						["secondary", "tertiary"],
						.1,
						0
					],
					13,
					[
						"match",
						["get", "class"],
						[
							"motorway",
							"trunk",
							"primary"
						],
						4,
						["secondary", "tertiary"],
						2.5,
						[
							"motorway_link",
							"trunk_link",
							"primary_link",
							"street",
							"street_limited"
						],
						1,
						.5
					],
					18,
					[
						"match",
						["get", "class"],
						[
							"motorway",
							"trunk",
							"primary"
						],
						32,
						["secondary", "tertiary"],
						26,
						[
							"motorway_link",
							"trunk_link",
							"primary_link",
							"street",
							"street_limited"
						],
						18,
						10
					]
				],
				"line-color": [
					"match",
					["get", "class"],
					[
						"primary_link",
						"secondary_link",
						"tertiary_link",
						"street",
						"street_limited",
						"service",
						"track"
					],
					"hsl(171, 38%, 95%)",
					"hsl(171, 38%, 100%)"
				]
			},
			"source-layer": "road"
		},
		{
			minzoom: 14,
			layout: {
				"line-join": "round",
				visibility: "none"
			},
			metadata: {
				"mapbox:featureComponent": "walking-cycling",
				"mapbox:group": "Walking, cycling, etc., barriers-bridges"
			},
			filter: [
				"all",
				[
					"==",
					["get", "structure"],
					"bridge"
				],
				[
					"==",
					["get", "class"],
					"path"
				],
				[
					"==",
					["geometry-type"],
					"LineString"
				],
				[
					"!=",
					["get", "type"],
					"steps"
				]
			],
			type: "line",
			source: "composite",
			id: "bridge-path",
			paint: {
				"line-width": [
					"interpolate",
					["exponential", 1.5],
					["zoom"],
					15,
					1,
					18,
					4
				],
				"line-color": "rgb(255, 255, 255)",
				"line-dasharray": [
					"step",
					["zoom"],
					["literal", [4, .3]],
					15,
					["literal", [1.75, .3]],
					16,
					["literal", [1, .3]],
					17,
					["literal", [1, .25]]
				]
			},
			"source-layer": "road"
		},
		{
			minzoom: 14,
			layout: {
				"line-join": "round",
				visibility: "none"
			},
			metadata: {
				"mapbox:featureComponent": "walking-cycling",
				"mapbox:group": "Walking, cycling, etc., barriers-bridges"
			},
			filter: [
				"all",
				[
					"==",
					["get", "type"],
					"steps"
				],
				[
					"==",
					["get", "structure"],
					"bridge"
				],
				[
					"==",
					["geometry-type"],
					"LineString"
				]
			],
			type: "line",
			source: "composite",
			id: "bridge-steps",
			paint: {
				"line-width": [
					"interpolate",
					["exponential", 1.5],
					["zoom"],
					15,
					1,
					16,
					1.6,
					18,
					6
				],
				"line-color": "rgb(255, 255, 255)",
				"line-dasharray": [
					"step",
					["zoom"],
					["literal", [1, 0]],
					15,
					["literal", [1.75, 1]],
					16,
					["literal", [1, .75]],
					17,
					["literal", [.3, .3]]
				]
			},
			"source-layer": "road"
		},
		{
			minzoom: 13,
			layout: {
				"line-join": "round",
				visibility: "none"
			},
			metadata: {
				"mapbox:featureComponent": "walking-cycling",
				"mapbox:group": "Walking, cycling, etc., barriers-bridges"
			},
			filter: [
				"all",
				[
					"==",
					["get", "structure"],
					"bridge"
				],
				[
					"==",
					["get", "class"],
					"pedestrian"
				],
				[
					"==",
					["geometry-type"],
					"LineString"
				]
			],
			type: "line",
			source: "composite",
			id: "bridge-pedestrian",
			paint: {
				"line-width": [
					"interpolate",
					["exponential", 1.5],
					["zoom"],
					14,
					.5,
					18,
					12
				],
				"line-color": "rgb(255, 255, 255)",
				"line-dasharray": [
					"step",
					["zoom"],
					["literal", [1, 0]],
					15,
					["literal", [1.5, .4]],
					16,
					["literal", [1, .2]]
				]
			},
			"source-layer": "road"
		},
		{
			minzoom: 13,
			layout: {
				"line-join": [
					"step",
					["zoom"],
					"miter",
					14,
					"round"
				],
				visibility: "none"
			},
			metadata: {
				"mapbox:featureComponent": "road-network",
				"mapbox:group": "Road network, bridges"
			},
			filter: [
				"all",
				[
					"==",
					["get", "structure"],
					"bridge"
				],
				[
					"step",
					["zoom"],
					[
						"match",
						["get", "class"],
						[
							"motorway",
							"motorway_link",
							"trunk",
							"trunk_link",
							"primary",
							"secondary",
							"tertiary",
							"street",
							"street_limited",
							"primary_link",
							"track"
						],
						true,
						false
					],
					14,
					[
						"match",
						["get", "class"],
						[
							"motorway",
							"motorway_link",
							"trunk",
							"trunk_link",
							"primary",
							"primary_link",
							"secondary",
							"secondary_link",
							"tertiary",
							"tertiary_link",
							"street",
							"street_limited",
							"service",
							"track"
						],
						true,
						false
					]
				],
				[
					"==",
					["geometry-type"],
					"LineString"
				]
			],
			type: "line",
			source: "composite",
			id: "bridge-case-simple",
			paint: {
				"line-width": [
					"interpolate",
					["exponential", 1.5],
					["zoom"],
					13,
					[
						"match",
						["get", "class"],
						[
							"motorway",
							"trunk",
							"primary"
						],
						6,
						["secondary", "tertiary"],
						4,
						[
							"motorway_link",
							"trunk_link",
							"street",
							"street_limited",
							"primary_link"
						],
						2.5,
						1.25
					],
					18,
					[
						"match",
						["get", "class"],
						[
							"motorway",
							"trunk",
							"primary"
						],
						36,
						["secondary", "tertiary"],
						30,
						[
							"motorway_link",
							"trunk_link",
							"street",
							"street_limited",
							"primary_link"
						],
						22,
						16
					]
				],
				"line-color": "rgb(235, 243, 245)"
			},
			"source-layer": "road"
		},
		{
			minzoom: 13,
			layout: {
				"line-cap": [
					"step",
					["zoom"],
					"butt",
					14,
					"round"
				],
				"line-join": [
					"step",
					["zoom"],
					"miter",
					14,
					"round"
				],
				visibility: "none"
			},
			metadata: {
				"mapbox:featureComponent": "road-network",
				"mapbox:group": "Road network, bridges"
			},
			filter: [
				"all",
				[
					"==",
					["get", "structure"],
					"bridge"
				],
				[
					"step",
					["zoom"],
					[
						"match",
						["get", "class"],
						["motorway", "trunk"],
						true,
						false
					],
					13,
					[
						"match",
						["get", "class"],
						[
							"motorway",
							"motorway_link",
							"trunk",
							"trunk_link",
							"primary",
							"secondary",
							"tertiary",
							"street",
							"street_limited",
							"primary_link",
							"track"
						],
						true,
						false
					],
					14,
					[
						"match",
						["get", "class"],
						[
							"motorway",
							"motorway_link",
							"trunk",
							"trunk_link",
							"primary",
							"primary_link",
							"secondary",
							"secondary_link",
							"tertiary",
							"tertiary_link",
							"street",
							"street_limited",
							"service",
							"track"
						],
						true,
						false
					]
				],
				[
					"==",
					["geometry-type"],
					"LineString"
				]
			],
			type: "line",
			source: "composite",
			id: "bridge-simple",
			paint: {
				"line-width": [
					"interpolate",
					["exponential", 1.5],
					["zoom"],
					13,
					[
						"match",
						["get", "class"],
						[
							"motorway",
							"trunk",
							"primary"
						],
						4,
						["secondary", "tertiary"],
						2.5,
						[
							"motorway_link",
							"trunk_link",
							"street",
							"street_limited",
							"primary_link"
						],
						1,
						.5
					],
					18,
					[
						"match",
						["get", "class"],
						[
							"motorway",
							"trunk",
							"primary"
						],
						32,
						["secondary", "tertiary"],
						26,
						[
							"motorway_link",
							"trunk_link",
							"street",
							"street_limited",
							"primary_link"
						],
						18,
						12
					]
				],
				"line-color": [
					"match",
					["get", "class"],
					[
						"primary_link",
						"secondary_link",
						"tertiary_link",
						"street",
						"street_limited",
						"service",
						"track"
					],
					"hsl(171, 38%, 95%)",
					"hsl(171, 38%, 100%)"
				]
			},
			"source-layer": "road"
		},
		{
			minzoom: 15,
			layout: { visibility: "none" },
			metadata: {
				"mapbox:featureComponent": "buildings",
				"mapbox:group": "Buildings, extruded"
			},
			filter: [
				"all",
				[
					"==",
					["get", "underground"],
					"false"
				],
				[
					"!=",
					["get", "extrude"],
					"false"
				]
			],
			type: "fill-extrusion",
			source: "composite",
			id: "building-extrusion",
			paint: {
				"fill-extrusion-color": "rgb(161, 199, 208)",
				"fill-extrusion-height": ["get", "height"],
				"fill-extrusion-opacity": .3
			},
			"source-layer": "building"
		},
		{
			minzoom: 7,
			layout: {
				"line-join": "bevel",
				visibility: "none"
			},
			metadata: {
				"mapbox:featureComponent": "admin-boundaries",
				"mapbox:group": "Administrative boundaries, admin"
			},
			filter: [
				"all",
				[
					"==",
					["get", "admin_level"],
					1
				],
				[
					"==",
					["get", "maritime"],
					"false"
				],
				[
					"match",
					["get", "worldview"],
					["all", "US"],
					true,
					false
				]
			],
			type: "line",
			source: "composite",
			id: "admin-1-boundary-bg",
			paint: {
				"line-color": [
					"interpolate",
					["linear"],
					["zoom"],
					8,
					"rgb(235, 243, 245)",
					16,
					"hsl(169, 14%, 94%)"
				],
				"line-width": [
					"interpolate",
					["linear"],
					["zoom"],
					7,
					3.75,
					12,
					5.5
				],
				"line-opacity": [
					"interpolate",
					["linear"],
					["zoom"],
					7,
					0,
					8,
					.75
				],
				"line-dasharray": [1, 0],
				"line-blur": [
					"interpolate",
					["linear"],
					["zoom"],
					3,
					0,
					8,
					3
				]
			},
			"source-layer": "admin"
		},
		{
			minzoom: 1,
			layout: { visibility: "none" },
			metadata: {
				"mapbox:featureComponent": "admin-boundaries",
				"mapbox:group": "Administrative boundaries, admin"
			},
			filter: [
				"all",
				[
					"==",
					["get", "admin_level"],
					0
				],
				[
					"==",
					["get", "maritime"],
					"false"
				],
				[
					"match",
					["get", "worldview"],
					["all", "US"],
					true,
					false
				]
			],
			type: "line",
			source: "composite",
			id: "admin-0-boundary-bg",
			paint: {
				"line-width": [
					"interpolate",
					["linear"],
					["zoom"],
					3,
					3.5,
					10,
					8
				],
				"line-color": [
					"interpolate",
					["linear"],
					["zoom"],
					6,
					"rgb(235, 243, 245)",
					8,
					"hsl(169, 14%, 94%)"
				],
				"line-opacity": [
					"interpolate",
					["linear"],
					["zoom"],
					3,
					0,
					4,
					.5
				],
				"line-blur": [
					"interpolate",
					["linear"],
					["zoom"],
					3,
					0,
					10,
					2
				]
			},
			"source-layer": "admin"
		},
		{
			minzoom: 2,
			layout: {
				"line-join": "round",
				"line-cap": "round",
				visibility: "none"
			},
			metadata: {
				"mapbox:featureComponent": "admin-boundaries",
				"mapbox:group": "Administrative boundaries, admin"
			},
			filter: [
				"all",
				[
					"==",
					["get", "admin_level"],
					1
				],
				[
					"==",
					["get", "maritime"],
					"false"
				],
				[
					"match",
					["get", "worldview"],
					["all", "US"],
					true,
					false
				]
			],
			type: "line",
			source: "composite",
			id: "admin-1-boundary",
			paint: {
				"line-dasharray": [
					"step",
					["zoom"],
					["literal", [2, 0]],
					7,
					["literal", [
						2,
						2,
						6,
						2
					]]
				],
				"line-width": [
					"interpolate",
					["linear"],
					["zoom"],
					7,
					.75,
					12,
					1.5
				],
				"line-opacity": [
					"interpolate",
					["linear"],
					["zoom"],
					2,
					0,
					3,
					1
				],
				"line-color": [
					"interpolate",
					["linear"],
					["zoom"],
					3,
					"hsl(169, 4%, 77%)",
					7,
					"hsl(169, 2%, 62%)"
				]
			},
			"source-layer": "admin"
		},
		{
			minzoom: 1,
			layout: {
				"line-join": "round",
				"line-cap": "round",
				visibility: "none"
			},
			metadata: {
				"mapbox:featureComponent": "admin-boundaries",
				"mapbox:group": "Administrative boundaries, admin"
			},
			filter: [
				"all",
				[
					"==",
					["get", "admin_level"],
					0
				],
				[
					"==",
					["get", "disputed"],
					"false"
				],
				[
					"==",
					["get", "maritime"],
					"false"
				],
				[
					"match",
					["get", "worldview"],
					["all", "US"],
					true,
					false
				]
			],
			type: "line",
			source: "composite",
			id: "admin-0-boundary",
			paint: {
				"line-color": "hsl(169, 2%, 51%)",
				"line-width": [
					"interpolate",
					["linear"],
					["zoom"],
					3,
					.5,
					10,
					2
				],
				"line-dasharray": [10, 0]
			},
			"source-layer": "admin"
		},
		{
			minzoom: 1,
			layout: {
				"line-join": "round",
				visibility: "none"
			},
			metadata: {
				"mapbox:featureComponent": "admin-boundaries",
				"mapbox:group": "Administrative boundaries, admin"
			},
			filter: [
				"all",
				[
					"==",
					["get", "disputed"],
					"true"
				],
				[
					"==",
					["get", "admin_level"],
					0
				],
				[
					"==",
					["get", "maritime"],
					"false"
				],
				[
					"match",
					["get", "worldview"],
					["all", "US"],
					true,
					false
				]
			],
			type: "line",
			source: "composite",
			id: "admin-0-boundary-disputed",
			paint: {
				"line-color": "hsl(169, 2%, 51%)",
				"line-width": [
					"interpolate",
					["linear"],
					["zoom"],
					3,
					.5,
					10,
					2
				],
				"line-dasharray": [
					"step",
					["zoom"],
					["literal", [3.25, 3.25]],
					6,
					["literal", [2.5, 2.5]],
					7,
					["literal", [2, 2.25]],
					8,
					["literal", [1.75, 2]]
				]
			},
			"source-layer": "admin"
		},
		{
			minzoom: 12,
			layout: {
				"text-size": [
					"interpolate",
					["linear"],
					["zoom"],
					10,
					[
						"match",
						["get", "class"],
						[
							"motorway",
							"trunk",
							"primary",
							"secondary",
							"tertiary"
						],
						10,
						9
					],
					18,
					[
						"match",
						["get", "class"],
						[
							"motorway",
							"trunk",
							"primary",
							"secondary",
							"tertiary"
						],
						16,
						14
					]
				],
				"text-max-angle": 30,
				"text-font": ["Arial"],
				"symbol-placement": "line",
				"text-padding": 1,
				visibility: "none",
				"text-rotation-alignment": "map",
				"text-pitch-alignment": "viewport",
				"text-field": [
					"coalesce",
					["get", "name_en"],
					["get", "name"]
				],
				"text-letter-spacing": .01
			},
			metadata: {
				"mapbox:featureComponent": "road-network",
				"mapbox:group": "Road network, road-labels"
			},
			filter: [
				"match",
				["get", "class"],
				[
					"motorway",
					"trunk",
					"primary",
					"secondary",
					"tertiary",
					"street",
					"street_limited"
				],
				true,
				false
			],
			type: "symbol",
			source: "composite",
			id: "road-label-simple",
			paint: {
				"text-color": "rgb(79, 142, 158)",
				"text-halo-color": "hsl(171, 38%, 100%)",
				"text-halo-width": 1
			},
			"source-layer": "road"
		},
		{
			minzoom: 12,
			layout: {
				"text-size": [
					"interpolate",
					["linear"],
					["zoom"],
					10,
					[
						"match",
						["get", "class"],
						"pedestrian",
						9,
						6.5
					],
					18,
					[
						"match",
						["get", "class"],
						"pedestrian",
						14,
						13
					]
				],
				"text-max-angle": 30,
				"text-font": ["Arial"],
				"symbol-placement": "line",
				"text-padding": 1,
				visibility: "none",
				"text-rotation-alignment": "map",
				"text-pitch-alignment": "viewport",
				"text-field": [
					"coalesce",
					["get", "name_en"],
					["get", "name"]
				],
				"text-letter-spacing": .01
			},
			metadata: {
				"mapbox:featureComponent": "walking-cycling",
				"mapbox:group": "Walking, cycling, etc., walking-cycling-labels"
			},
			filter: [
				"step",
				["zoom"],
				[
					"match",
					["get", "class"],
					["pedestrian"],
					true,
					false
				],
				15,
				[
					"match",
					["get", "class"],
					["path", "pedestrian"],
					true,
					false
				]
			],
			type: "symbol",
			source: "composite",
			id: "path-pedestrian-label",
			paint: {
				"text-color": "rgb(79, 142, 158)",
				"text-halo-color": "rgb(255, 255, 255)",
				"text-halo-width": 1,
				"text-halo-blur": 1
			},
			"source-layer": "road"
		},
		{
			minzoom: 13,
			layout: {
				"text-font": ["Arial"],
				"text-max-angle": 30,
				"symbol-spacing": [
					"interpolate",
					["linear", 1],
					["zoom"],
					15,
					250,
					17,
					400
				],
				"text-size": [
					"interpolate",
					["linear"],
					["zoom"],
					13,
					12,
					18,
					16
				],
				"symbol-placement": "line",
				"text-pitch-alignment": "viewport",
				"text-field": [
					"coalesce",
					["get", "name_en"],
					["get", "name"]
				],
				visibility: "none"
			},
			metadata: {
				"mapbox:featureComponent": "natural-features",
				"mapbox:group": "Natural features, natural-labels"
			},
			filter: [
				"all",
				[
					"match",
					["get", "class"],
					[
						"canal",
						"river",
						"stream"
					],
					[
						"match",
						["get", "worldview"],
						["all", "US"],
						true,
						false
					],
					[
						"disputed_canal",
						"disputed_river",
						"disputed_stream"
					],
					[
						"all",
						[
							"==",
							["get", "disputed"],
							"true"
						],
						[
							"match",
							["get", "worldview"],
							["all", "US"],
							true,
							false
						]
					],
					false
				],
				[
					"==",
					["geometry-type"],
					"LineString"
				]
			],
			type: "symbol",
			source: "composite",
			id: "waterway-label",
			paint: { "text-color": "hsl(197, 1%, 57%)" },
			"source-layer": "natural_label"
		},
		{
			minzoom: 4,
			layout: {
				"text-size": [
					"step",
					["zoom"],
					[
						"step",
						["get", "sizerank"],
						18,
						5,
						12
					],
					17,
					[
						"step",
						["get", "sizerank"],
						18,
						13,
						12
					]
				],
				"text-max-angle": 30,
				"text-field": [
					"coalesce",
					["get", "name_en"],
					["get", "name"]
				],
				"text-font": ["Arial"],
				"symbol-placement": "line-center",
				"text-pitch-alignment": "viewport",
				visibility: "none"
			},
			metadata: {
				"mapbox:featureComponent": "natural-features",
				"mapbox:group": "Natural features, natural-labels"
			},
			filter: [
				"all",
				[
					"match",
					["get", "class"],
					["glacier", "landform"],
					[
						"match",
						["get", "worldview"],
						["all", "US"],
						true,
						false
					],
					["disputed_glacier", "disputed_landform"],
					[
						"all",
						[
							"==",
							["get", "disputed"],
							"true"
						],
						[
							"match",
							["get", "worldview"],
							["all", "US"],
							true,
							false
						]
					],
					false
				],
				[
					"==",
					["geometry-type"],
					"LineString"
				],
				[
					"<=",
					["get", "filterrank"],
					2
				]
			],
			type: "symbol",
			source: "composite",
			id: "natural-line-label",
			paint: {
				"text-halo-width": .5,
				"text-halo-color": "rgb(255, 255, 255)",
				"text-halo-blur": .5,
				"text-color": [
					"step",
					["zoom"],
					[
						"step",
						["get", "sizerank"],
						"hsl(202, 0%, 66%)",
						5,
						"hsl(202, 4%, 56%)"
					],
					17,
					[
						"step",
						["get", "sizerank"],
						"hsl(202, 0%, 66%)",
						13,
						"hsl(202, 4%, 56%)"
					]
				]
			},
			"source-layer": "natural_label"
		},
		{
			minzoom: 4,
			layout: {
				"text-size": [
					"step",
					["zoom"],
					[
						"step",
						["get", "sizerank"],
						18,
						5,
						12
					],
					17,
					[
						"step",
						["get", "sizerank"],
						18,
						13,
						12
					]
				],
				"icon-image": [
					"step",
					["zoom"],
					[
						"concat",
						["get", "maki"],
						"-11"
					],
					15,
					[
						"concat",
						["get", "maki"],
						"-15"
					]
				],
				"text-font": ["Arial"],
				"text-offset": [
					"step",
					["zoom"],
					[
						"step",
						["get", "sizerank"],
						["literal", [0, 0]],
						5,
						["literal", [0, .75]]
					],
					17,
					[
						"step",
						["get", "sizerank"],
						["literal", [0, 0]],
						13,
						["literal", [0, .75]]
					]
				],
				"text-anchor": [
					"step",
					["zoom"],
					[
						"step",
						["get", "sizerank"],
						"center",
						5,
						"top"
					],
					17,
					[
						"step",
						["get", "sizerank"],
						"center",
						13,
						"top"
					]
				],
				"text-field": [
					"coalesce",
					["get", "name_en"],
					["get", "name"]
				],
				visibility: "none"
			},
			metadata: {
				"mapbox:featureComponent": "natural-features",
				"mapbox:group": "Natural features, natural-labels"
			},
			filter: [
				"all",
				[
					"match",
					["get", "class"],
					[
						"dock",
						"glacier",
						"landform",
						"water_feature",
						"wetland"
					],
					[
						"match",
						["get", "worldview"],
						["all", "US"],
						true,
						false
					],
					[
						"disputed_dock",
						"disputed_glacier",
						"disputed_landform",
						"disputed_water_feature",
						"disputed_wetland"
					],
					[
						"all",
						[
							"==",
							["get", "disputed"],
							"true"
						],
						[
							"match",
							["get", "worldview"],
							["all", "US"],
							true,
							false
						]
					],
					false
				],
				[
					"==",
					["geometry-type"],
					"Point"
				],
				[
					"<=",
					["get", "filterrank"],
					2
				]
			],
			type: "symbol",
			source: "composite",
			id: "natural-point-label",
			paint: {
				"icon-opacity": [
					"step",
					["zoom"],
					[
						"step",
						["get", "sizerank"],
						0,
						5,
						1
					],
					17,
					[
						"step",
						["get", "sizerank"],
						0,
						13,
						1
					]
				],
				"text-halo-color": "rgb(255, 255, 255)",
				"text-halo-width": .5,
				"text-halo-blur": .5,
				"text-color": [
					"step",
					["zoom"],
					[
						"step",
						["get", "sizerank"],
						"hsl(202, 0%, 66%)",
						5,
						"hsl(202, 4%, 56%)"
					],
					17,
					[
						"step",
						["get", "sizerank"],
						"hsl(202, 0%, 66%)",
						13,
						"hsl(202, 4%, 56%)"
					]
				]
			},
			"source-layer": "natural_label"
		},
		{
			id: "water-line-label",
			type: "symbol",
			metadata: {
				"mapbox:featureComponent": "natural-features",
				"mapbox:group": "Natural features, natural-labels"
			},
			source: "composite",
			"source-layer": "natural_label",
			filter: [
				"all",
				[
					"match",
					["get", "class"],
					[
						"bay",
						"ocean",
						"reservoir",
						"sea",
						"water"
					],
					[
						"match",
						["get", "worldview"],
						["all", "US"],
						true,
						false
					],
					[
						"disputed_bay",
						"disputed_ocean",
						"disputed_reservoir",
						"disputed_sea",
						"disputed_water"
					],
					[
						"all",
						[
							"==",
							["get", "disputed"],
							"true"
						],
						[
							"match",
							["get", "worldview"],
							["all", "US"],
							true,
							false
						]
					],
					false
				],
				[
					"==",
					["geometry-type"],
					"LineString"
				]
			],
			layout: {
				"text-size": [
					"interpolate",
					["linear"],
					["zoom"],
					7,
					[
						"step",
						["get", "sizerank"],
						20,
						6,
						18,
						12,
						12
					],
					10,
					[
						"step",
						["get", "sizerank"],
						15,
						9,
						12
					],
					18,
					[
						"step",
						["get", "sizerank"],
						15,
						9,
						14
					]
				],
				"text-max-angle": 30,
				"text-letter-spacing": [
					"match",
					["get", "class"],
					"ocean",
					.25,
					["sea", "bay"],
					.15,
					0
				],
				"text-font": ["Arial"],
				"symbol-placement": "line-center",
				"text-pitch-alignment": "viewport",
				"text-field": [
					"coalesce",
					["get", "name_en"],
					["get", "name"]
				],
				visibility: "none"
			},
			paint: { "text-color": [
				"match",
				["get", "class"],
				[
					"bay",
					"ocean",
					"sea"
				],
				"hsl(197, 29%, 61%)",
				"hsl(197, 1%, 57%)"
			] }
		},
		{
			id: "water-point-label",
			type: "symbol",
			source: "composite",
			"source-layer": "natural_label",
			filter: [
				"all",
				[
					"match",
					["get", "class"],
					[
						"bay",
						"ocean",
						"reservoir",
						"sea",
						"water"
					],
					[
						"match",
						["get", "worldview"],
						["all", "US"],
						true,
						false
					],
					[
						"disputed_bay",
						"disputed_ocean",
						"disputed_reservoir",
						"disputed_sea",
						"disputed_water"
					],
					[
						"all",
						[
							"==",
							["get", "disputed"],
							"true"
						],
						[
							"match",
							["get", "worldview"],
							["all", "US"],
							true,
							false
						]
					],
					false
				],
				[
					"==",
					["geometry-type"],
					"Point"
				]
			],
			layout: {
				"text-line-height": 1.3,
				"text-size": [
					"interpolate",
					["linear"],
					["zoom"],
					7,
					[
						"step",
						["get", "sizerank"],
						20,
						6,
						15,
						12,
						12
					],
					10,
					[
						"step",
						["get", "sizerank"],
						15,
						9,
						12
					]
				],
				"text-font": ["Arial"],
				"text-field": [
					"coalesce",
					["get", "name_en"],
					["get", "name"]
				],
				"text-letter-spacing": [
					"match",
					["get", "class"],
					"ocean",
					.25,
					["bay", "sea"],
					.15,
					.01
				],
				"text-max-width": [
					"match",
					["get", "class"],
					"ocean",
					4,
					"sea",
					5,
					["bay", "water"],
					7,
					10
				],
				visibility: "none"
			},
			paint: { "text-color": [
				"match",
				["get", "class"],
				[
					"bay",
					"ocean",
					"sea"
				],
				"hsl(197, 29%, 61%)",
				"hsl(197, 1%, 57%)"
			] },
			metadata: {
				"mapbox:featureComponent": "natural-features",
				"mapbox:group": "Natural features, natural-labels"
			}
		},
		{
			minzoom: 6,
			layout: {
				"text-size": [
					"step",
					["zoom"],
					[
						"step",
						["get", "sizerank"],
						18,
						5,
						12
					],
					17,
					[
						"step",
						["get", "sizerank"],
						18,
						13,
						12
					]
				],
				"icon-image": [
					"step",
					["zoom"],
					[
						"case",
						["has", "maki_beta"],
						["image", [
							"concat",
							["get", "maki_beta"],
							"-11"
						]],
						["image", [
							"concat",
							["get", "maki"],
							"-11"
						]]
					],
					15,
					[
						"case",
						["has", "maki_beta"],
						["image", [
							"concat",
							["get", "maki_beta"],
							"-15"
						]],
						["image", [
							"concat",
							["get", "maki"],
							"-15"
						]]
					]
				],
				"text-font": ["Arial"],
				"text-offset": [
					"step",
					["zoom"],
					[
						"step",
						["get", "sizerank"],
						["literal", [0, 0]],
						5,
						["literal", [0, .75]]
					],
					17,
					[
						"step",
						["get", "sizerank"],
						["literal", [0, 0]],
						13,
						["literal", [0, .75]]
					]
				],
				"text-anchor": [
					"step",
					["zoom"],
					[
						"step",
						["get", "sizerank"],
						"center",
						5,
						"top"
					],
					17,
					[
						"step",
						["get", "sizerank"],
						"center",
						13,
						"top"
					]
				],
				"text-field": [
					"coalesce",
					["get", "name_en"],
					["get", "name"]
				],
				visibility: "none"
			},
			metadata: {
				"mapbox:featureComponent": "point-of-interest-labels",
				"mapbox:group": "Point of interest labels, poi-labels"
			},
			filter: [
				"<=",
				["get", "filterrank"],
				[
					"+",
					[
						"step",
						["zoom"],
						0,
						16,
						1,
						17,
						2
					],
					2
				]
			],
			type: "symbol",
			source: "composite",
			id: "poi-label",
			paint: {
				"icon-opacity": [
					"step",
					["zoom"],
					[
						"step",
						["get", "sizerank"],
						0,
						5,
						1
					],
					17,
					[
						"step",
						["get", "sizerank"],
						0,
						13,
						1
					]
				],
				"text-halo-color": [
					"match",
					["get", "class"],
					"park_like",
					"hsl(133, 43%, 100%)",
					"education",
					"hsl(148, 22%, 100%)",
					"medical",
					"hsl(200, 16%, 100%)",
					"rgb(255, 255, 255)"
				],
				"text-halo-width": .5,
				"text-halo-blur": .5,
				"text-color": [
					"step",
					["zoom"],
					[
						"step",
						["get", "sizerank"],
						[
							"match",
							["get", "class"],
							"food_and_drink",
							"hsl(198, 9%, 79%)",
							"park_like",
							"hsl(134, 18%, 54%)",
							"education",
							"hsl(148, 0%, 52%)",
							"medical",
							"hsl(200, 2%, 53%)",
							"hsl(202, 0%, 66%)"
						],
						5,
						[
							"match",
							["get", "class"],
							"food_and_drink",
							"hsl(198, 14%, 62%)",
							"park_like",
							"hsl(134, 17%, 43%)",
							"education",
							"hsl(148, 0%, 32%)",
							"medical",
							"hsl(200, 3%, 43%)",
							"hsl(202, 4%, 56%)"
						]
					],
					17,
					[
						"step",
						["get", "sizerank"],
						[
							"match",
							["get", "class"],
							"food_and_drink",
							"hsl(198, 9%, 79%)",
							"park_like",
							"hsl(134, 18%, 54%)",
							"education",
							"hsl(148, 0%, 52%)",
							"medical",
							"hsl(200, 2%, 53%)",
							"hsl(202, 0%, 66%)"
						],
						13,
						[
							"match",
							["get", "class"],
							"food_and_drink",
							"hsl(198, 14%, 62%)",
							"park_like",
							"hsl(134, 17%, 43%)",
							"education",
							"hsl(148, 0%, 32%)",
							"medical",
							"hsl(200, 3%, 43%)",
							"hsl(202, 4%, 56%)"
						]
					]
				]
			},
			"source-layer": "poi_label"
		},
		{
			minzoom: 8,
			layout: {
				"text-line-height": 1.1,
				"text-size": [
					"step",
					["get", "sizerank"],
					18,
					9,
					12
				],
				"icon-image": [
					"step",
					["get", "sizerank"],
					[
						"concat",
						["get", "maki"],
						"-15"
					],
					9,
					[
						"concat",
						["get", "maki"],
						"-11"
					]
				],
				"text-font": ["Arial"],
				visibility: "none",
				"text-offset": [0, .75],
				"text-rotation-alignment": "viewport",
				"text-anchor": "top",
				"text-field": [
					"step",
					["get", "sizerank"],
					[
						"coalesce",
						["get", "name_en"],
						["get", "name"]
					],
					15,
					["get", "ref"]
				],
				"text-letter-spacing": .01,
				"text-max-width": 9
			},
			metadata: {
				"mapbox:featureComponent": "transit",
				"mapbox:group": "Transit, transit-labels"
			},
			filter: [
				"match",
				["get", "class"],
				["military", "civil"],
				[
					"match",
					["get", "worldview"],
					["all", "US"],
					true,
					false
				],
				["disputed_military", "disputed_civil"],
				[
					"all",
					[
						"==",
						["get", "disputed"],
						"true"
					],
					[
						"match",
						["get", "worldview"],
						["all", "US"],
						true,
						false
					]
				],
				false
			],
			type: "symbol",
			source: "composite",
			id: "airport-label",
			paint: {
				"text-color": "hsl(135, 6%, 40%)",
				"text-halo-color": "hsl(135, 53%, 100%)",
				"text-halo-width": 1
			},
			"source-layer": "airport_label"
		},
		{
			minzoom: 10,
			layout: {
				"text-field": [
					"coalesce",
					["get", "name_ru"],
					["get", "name"]
				],
				"text-transform": "uppercase",
				"text-font": ["Arial"],
				"text-letter-spacing": [
					"match",
					["get", "type"],
					"suburb",
					.15,
					.1
				],
				"text-max-width": 7,
				"text-padding": 3,
				"text-size": [
					"interpolate",
					[
						"cubic-bezier",
						.5,
						0,
						1,
						1
					],
					["zoom"],
					11,
					[
						"match",
						["get", "type"],
						"suburb",
						11,
						10.5
					],
					15,
					[
						"match",
						["get", "type"],
						"suburb",
						15,
						14
					]
				],
				visibility: "none"
			},
			metadata: {
				"mapbox:featureComponent": "place-labels",
				"mapbox:group": "Place labels, place-labels"
			},
			maxzoom: 15,
			filter: [
				"all",
				[
					"match",
					["get", "class"],
					"settlement_subdivision",
					[
						"match",
						["get", "worldview"],
						["all", "US"],
						true,
						false
					],
					"disputed_settlement_subdivision",
					[
						"all",
						[
							"==",
							["get", "disputed"],
							"true"
						],
						[
							"match",
							["get", "worldview"],
							["all", "US"],
							true,
							false
						]
					],
					false
				],
				[
					"<=",
					["get", "filterrank"],
					3
				]
			],
			type: "symbol",
			source: "composite",
			id: "settlement-subdivision-label",
			paint: {
				"text-halo-color": "rgba(255, 255, 255, 0.75)",
				"text-halo-width": 1,
				"text-color": "hsl(169, 8%, 55%)",
				"text-halo-blur": .5
			},
			"source-layer": "place_label"
		},
		{
			layout: {
				"text-line-height": 1.1,
				"text-size": [
					"interpolate",
					[
						"cubic-bezier",
						.2,
						0,
						.9,
						1
					],
					["zoom"],
					3,
					[
						"step",
						["get", "symbolrank"],
						12,
						9,
						11,
						10,
						10.5,
						12,
						9.5,
						14,
						8.5,
						16,
						6.5,
						17,
						4
					],
					13,
					[
						"step",
						["get", "symbolrank"],
						23,
						9,
						21,
						10,
						19,
						11,
						17,
						12,
						16,
						13,
						15,
						15,
						13
					]
				],
				"icon-image": "",
				"text-font": ["Arial"],
				visibility: "none",
				"text-offset": [
					"step",
					["zoom"],
					["literal", [0, 0]],
					8,
					["literal", [0, 0]]
				],
				"text-anchor": [
					"step",
					["zoom"],
					"center",
					8,
					"center"
				],
				"text-field": [
					"coalesce",
					["get", "name_ru"],
					["get", "name"]
				],
				"text-max-width": 7
			},
			metadata: {
				"mapbox:featureComponent": "place-labels",
				"mapbox:group": "Place labels, place-labels"
			},
			maxzoom: 15,
			filter: [
				"all",
				[
					"<=",
					["get", "filterrank"],
					3
				],
				[
					"match",
					["get", "class"],
					"settlement",
					[
						"match",
						["get", "worldview"],
						["all", "US"],
						true,
						false
					],
					"disputed_settlement",
					[
						"all",
						[
							"==",
							["get", "disputed"],
							"true"
						],
						[
							"match",
							["get", "worldview"],
							["all", "US"],
							true,
							false
						]
					],
					false
				],
				[
					"step",
					["zoom"],
					true,
					8,
					[
						">=",
						["get", "symbolrank"],
						11
					],
					10,
					[
						">=",
						["get", "symbolrank"],
						12
					],
					11,
					[
						">=",
						["get", "symbolrank"],
						13
					],
					12,
					[
						">=",
						["get", "symbolrank"],
						15
					],
					13,
					[
						">=",
						["get", "symbolrank"],
						11
					],
					14,
					[
						">=",
						["get", "symbolrank"],
						13
					]
				]
			],
			type: "symbol",
			source: "composite",
			id: "settlement-minor-label",
			paint: {
				"text-color": "hsl(169, 8%, 34%)",
				"text-halo-color": "rgb(255, 255, 255)",
				"text-halo-width": 1,
				"icon-opacity": [
					"step",
					["zoom"],
					1,
					8,
					0
				],
				"text-halo-blur": 1
			},
			"source-layer": "place_label"
		},
		{
			layout: {
				"text-line-height": 1.1,
				"text-size": [
					"interpolate",
					[
						"cubic-bezier",
						.2,
						0,
						.9,
						1
					],
					["zoom"],
					8,
					[
						"step",
						["get", "symbolrank"],
						18,
						9,
						17,
						10,
						15
					],
					15,
					[
						"step",
						["get", "symbolrank"],
						23,
						9,
						22,
						10,
						20,
						11,
						18,
						12,
						16,
						13,
						15,
						15,
						13
					]
				],
				"icon-image": "",
				"text-font": ["Arial"],
				visibility: "none",
				"text-offset": [
					"step",
					["zoom"],
					["literal", [0, 0]],
					8,
					["literal", [0, 0]]
				],
				"text-anchor": [
					"step",
					["zoom"],
					"center",
					8,
					"center"
				],
				"text-field": [
					"coalesce",
					["get", "name_ru"],
					["get", "name"]
				],
				"text-max-width": 7
			},
			metadata: {
				"mapbox:featureComponent": "place-labels",
				"mapbox:group": "Place labels, place-labels"
			},
			maxzoom: 15,
			filter: [
				"all",
				[
					"<=",
					["get", "filterrank"],
					3
				],
				[
					"match",
					["get", "class"],
					"settlement",
					[
						"match",
						["get", "worldview"],
						["all", "US"],
						true,
						false
					],
					"disputed_settlement",
					[
						"all",
						[
							"==",
							["get", "disputed"],
							"true"
						],
						[
							"match",
							["get", "worldview"],
							["all", "US"],
							true,
							false
						]
					],
					false
				],
				[
					"step",
					["zoom"],
					false,
					8,
					[
						"<",
						["get", "symbolrank"],
						11
					],
					10,
					[
						"<",
						["get", "symbolrank"],
						12
					],
					11,
					[
						"<",
						["get", "symbolrank"],
						13
					],
					12,
					[
						"<",
						["get", "symbolrank"],
						15
					],
					13,
					[
						">=",
						["get", "symbolrank"],
						11
					],
					14,
					[
						">=",
						["get", "symbolrank"],
						13
					]
				]
			],
			type: "symbol",
			source: "composite",
			id: "settlement-major-label",
			paint: {
				"text-color": "hsl(169, 8%, 34%)",
				"text-halo-color": "rgb(255, 255, 255)",
				"text-halo-width": 1,
				"icon-opacity": [
					"step",
					["zoom"],
					1,
					8,
					0
				],
				"text-halo-blur": 1
			},
			"source-layer": "place_label"
		},
		{
			minzoom: 3,
			layout: {
				"text-size": [
					"interpolate",
					[
						"cubic-bezier",
						.85,
						.7,
						.65,
						1
					],
					["zoom"],
					4,
					[
						"step",
						["get", "symbolrank"],
						10,
						6,
						9.5,
						7,
						9
					],
					9,
					[
						"step",
						["get", "symbolrank"],
						21,
						6,
						16,
						7,
						13
					]
				],
				"text-transform": "uppercase",
				"text-font": ["Arial"],
				"text-field": [
					"coalesce",
					["get", "name_ru"],
					["get", "name"]
				],
				"text-letter-spacing": .15,
				"text-max-width": 6,
				visibility: "none"
			},
			metadata: {
				"mapbox:featureComponent": "place-labels",
				"mapbox:group": "Place labels, place-labels"
			},
			maxzoom: 9,
			filter: [
				"match",
				["get", "class"],
				"state",
				[
					"match",
					["get", "worldview"],
					["all", "US"],
					true,
					false
				],
				"disputed_state",
				[
					"all",
					[
						"==",
						["get", "disputed"],
						"true"
					],
					[
						"match",
						["get", "worldview"],
						["all", "US"],
						true,
						false
					]
				],
				false
			],
			type: "symbol",
			source: "composite",
			id: "state-label",
			paint: {
				"text-color": "hsl(169, 8%, 34%)",
				"text-halo-color": "rgb(255, 255, 255)",
				"text-halo-width": 1
			},
			"source-layer": "place_label"
		},
		{
			minzoom: 1,
			layout: {
				"icon-image": "",
				"text-field": [
					"coalesce",
					["get", "name_ru"],
					["get", "name"]
				],
				"text-line-height": 1.1,
				"text-max-width": 6,
				"text-font": ["Arial"],
				"text-offset": ["literal", [0, 0]],
				"text-size": [
					"interpolate",
					[
						"cubic-bezier",
						.2,
						0,
						.7,
						1
					],
					["zoom"],
					1,
					[
						"step",
						["get", "symbolrank"],
						11,
						4,
						9,
						5,
						8
					],
					9,
					[
						"step",
						["get", "symbolrank"],
						22,
						4,
						19,
						5,
						17
					]
				],
				visibility: "none"
			},
			metadata: {
				"mapbox:featureComponent": "place-labels",
				"mapbox:group": "Place labels, place-labels"
			},
			maxzoom: 10,
			filter: [
				"match",
				["get", "class"],
				"country",
				[
					"match",
					["get", "worldview"],
					["all", "US"],
					true,
					false
				],
				"disputed_country",
				[
					"all",
					[
						"==",
						["get", "disputed"],
						"true"
					],
					[
						"match",
						["get", "worldview"],
						["all", "US"],
						true,
						false
					]
				],
				false
			],
			type: "symbol",
			source: "composite",
			id: "country-label",
			paint: {
				"icon-opacity": [
					"step",
					["zoom"],
					[
						"case",
						["has", "text_anchor"],
						1,
						0
					],
					7,
					0
				],
				"text-color": "hsl(169, 8%, 34%)",
				"text-halo-color": [
					"interpolate",
					["linear"],
					["zoom"],
					2,
					"rgba(255, 255, 255, 0.75)",
					3,
					"rgb(255, 255, 255)"
				],
				"text-halo-width": 1.25
			},
			"source-layer": "place_label"
		},
		{
			id: "custom-road",
			minzoom: 5,
			type: "line",
			source: "composite",
			"source-layer": "road",
			paint: { "line-color": "hsl(0, 0%, 32%)" },
			layout: { visibility: "none" }
		}
	],
	created: "2021-02-17T11:35:35.621Z",
	modified: "2021-08-16T19:08:50.168Z",
	id: "ckl9czve40tc817mx7hz9gaig",
	owner: "soutjuseppe",
	visibility: "private",
	draft: true
};
//#endregion
//#region src/lib/localeDate.ts
/**
* Форматирует timestamp в локализованную строку даты/времени (`ru-RU`).
*
* @example localeDate(1700000000000, "dateTime") // "15 ноября 2023 г., 10:13"
* @example localeDate(1700000000000, "shortDate") // "15.11.23"
*
* @param date - timestamp в миллисекундах (число или строка)
* @param type - формат вывода; по умолчанию `dateTime`
* @returns локализованная строка даты/времени
*/
const localeDate = (date, type = "dateTime") => {
	let options;
	switch (type) {
		case "dateTime":
			options = {
				year: "numeric",
				month: "long",
				day: "numeric",
				hour: "numeric",
				minute: "numeric"
			};
			break;
		case "date":
			options = {
				year: "numeric",
				month: "long",
				day: "numeric"
			};
			break;
		case "shortDateTime":
			options = {
				year: "2-digit",
				month: "2-digit",
				day: "2-digit",
				hour: "2-digit",
				minute: "2-digit"
			};
			break;
		case "dateForInput":
			options = {
				day: "numeric",
				month: "numeric",
				year: "numeric"
			};
			break;
		default: options = {
			year: "2-digit",
			month: "2-digit",
			day: "2-digit"
		};
	}
	return (/* @__PURE__ */ new Date(+date)).toLocaleDateString("ru-RU", options);
};
//#endregion
//#region src/lib/groupby.ts
/**
* Группирует элементы массива по значению указанного ключа.
*
* @example
* groupBy(
*   [{ type: "a", id: 1 }, { type: "b", id: 2 }, { type: "a", id: 3 }],
*   "type",
* ) // { a: [{ type: "a", id: 1 }, { type: "a", id: 3 }], b: [{ type: "b", id: 2 }] }
*
* @param arr - исходный массив
* @param key - ключ поля для группировки
* @returns объект, где ключ — значение поля, значение — массив элементов
*/
const groupBy = (arr, key) => {
	return arr.reduce((rv, x) => {
		(rv[x[key]] = rv[x[key]] || []).push(x);
		return rv;
	}, {});
};
//#endregion
//#region src/lib/formatBytes.ts
/**
* Форматирует размер в байтах в человекочитаемую строку (1024-based).
*
* @example formatBytes(0) // "0 bytes"
* @example formatBytes(1536) // "1.5 KB"
* @example formatBytes(1073741824) // "1 GB"
* @example formatBytes(1099511627776) // "1 TB"
* @example formatBytes(1649267441664, 1) // "1.5 TB"
*
* @param bytes - размер в байтах
* @param decimals - количество знаков после запятой; отрицательное значение трактуется как `0`
* @returns строка вида `"1.5 KB"`
*/
function formatBytes(bytes, decimals = 2) {
	if (bytes === 0) return "0 bytes";
	const k = 1024;
	const dm = decimals < 0 ? 0 : decimals;
	const sizes = [
		"bytes",
		"KB",
		"MB",
		"GB",
		"TB",
		"PB",
		"EB",
		"ZB",
		"YB"
	];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
}
//#endregion
//#region src/lib/three.ts
const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath("/draco/");
dracoLoader.setDecoderConfig({ type: "js" });
const GLTFLoader = new GLTFLoader$1();
GLTFLoader.setDRACOLoader(dracoLoader);
//#endregion
//#region src/lib/getBrowser.ts
/**
* Определяет браузер и его версию по `navigator.userAgent`.
*
* @example getBrowser() // { name: "Chrome", version: "120" }
*
* @returns имя браузера и строку версии
*/
const getBrowser = () => {
	const ua = navigator.userAgent;
	let tem;
	let M = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];
	if (/trident/i.test(M[1] ?? "")) {
		tem = /\brv[ :]+(\d+)/g.exec(ua) || [];
		return {
			name: "IE",
			version: tem[1] || ""
		};
	}
	if (M[1] === "Chrome") {
		tem = ua.match(/\bOPR|Edge\/(\d+)/);
		if (tem != null) return {
			name: "Opera",
			version: tem[1]
		};
	}
	M = M[2] ? [M[1], M[2]] : [
		navigator.appName,
		navigator.appVersion,
		"-?"
	];
	if ((tem = ua.match(/version\/(\d+)/i)) != null) M.splice(1, 1, tem[1]);
	return {
		name: M[0] ?? "",
		version: M[1]
	};
};
//#endregion
//#region src/lib/isColorTooBlack.ts
/**
* Проверяет, слишком ли тёмный hex-цвет для контрастного текста/иконок на тёмном фоне.
*
* Использует относительную яркость по ITU-R BT.709; порог — `luma < 40`.
*
* @example isColorTooBlack("#000000") // true
* @example isColorTooBlack("#ffffff") // false
*
* @param hex - цвет в формате `#rrggbb`
* @returns `true`, если цвет слишком тёмный
*/
const isColorTooBlack = (hex) => {
	const code = hex.substring(1);
	const rgb = parseInt(code, 16);
	const r = rgb >> 16 & 255;
	const g = rgb >> 8 & 255;
	const b = rgb >> 0 & 255;
	if (.2126 * r + .7152 * g + .0722 * b < 40) return true;
	return false;
};
//#endregion
//#region src/lib/sliceIntoChunks.ts
/**
* Разбивает массив на равные чанки
*
* @example sliceIntoChunks([1, 2, 3, 4, 5, 6, 7, 8, 9, 10], 3) -> [[1, 2, 3], [4, 5, 6], [7, 8, 9], [10]]
*
* @param arr - исходный массив
* @param chunkSize - количество элементов в чанке
* @returns массив с чанками
*/
const sliceIntoChunks = (arr, chunkSize) => {
	const res = [];
	for (let i = 0; i < arr.length; i += chunkSize) {
		const chunk = arr.slice(i, i + chunkSize);
		res.push(chunk);
	}
	return res;
};
//#endregion
//#region src/lib/splitters.ts
/**
* Разбивает массив на чанки заданной длины.
*
* @example arraySplitter([1, 2, 3, 4, 5], 2) -> [[1, 2], [3, 4], [5]]
*
* @param arr - исходный массив
* @param length - количество элементов в чанке
* @returns массив с чанками; последний чанк может быть короче
*/
const arraySplitter = (arr, length) => {
	return arr.reduce((acc, item, index) => {
		if (index % length === 0) acc.push([]);
		acc[acc.length - 1].push(item);
		return acc;
	}, []);
};
/**
* Выполняет асинхронный callback для каждого чанка параллельно.
*
* @example
* await requestSplitter(
*   (batch) => api.fetchItems(batch),
*   arraySplitter(ids, 50),
* ) // -> массив ответов в порядке чанков
*
* @param cb - функция обработки одного чанка
* @param splittedArray - массив чанков (например, результат {@link arraySplitter})
* @returns массив результатов callback в порядке чанков; пустой массив, если вход пустой или первый чанк пустой
*/
const requestSplitter = async (cb, splittedArray) => {
	if (splittedArray.length === 0 || splittedArray[0].length === 0) return [];
	return await Promise.all(splittedArray.map((group) => cb(group)));
};
//#endregion
//#region src/lib/getNoun.ts
/**
* Выбирает форму существительного по числу (русская плюрализация).
*
* @example getNoun(1, "камень", "камня", "камней") // "камень"
* @example getNoun(2, "камень", "камня", "камней") // "камня"
* @example getNoun(11, "камень", "камня", "камней") // "камней"
*
* @param num - число элементов
* @param one - форма для 1, 21, 31, … (кроме 11)
* @param two - форма для 2–4, 22–24, … (кроме 12–14)
* @param five - форма для 0, 5–20 и остальных
* @returns подходящее числительное
*/
const getNoun = (num, one, two, five) => {
	let n = Math.abs(num);
	n %= 100;
	if (n >= 5 && n <= 20) return five;
	n %= 10;
	if (n === 1) return one;
	if (n >= 2 && n <= 4) return two;
	return five;
};
//#endregion
//#region src/lib/logger.ts
const pinoLogger = pino({
	browser: { asObject: true },
	level: import.meta.env.DEV ? "debug" : "info",
	transport: {
		target: "pino-pretty",
		options: { colorize: true }
	}
});
/**
* Обёртка для совместимости с Pino v10: преобразует вызовы
* logger.error('msg', error) -> logger.error({ err: error }, 'msg')
*/
function wrapLogMethod(method) {
	return function(objOrMsg, msgOrErr, ...args) {
		if (typeof objOrMsg === "string" && msgOrErr !== void 0) return method.call(pinoLogger, { err: msgOrErr }, objOrMsg, ...args);
		return method.call(pinoLogger, objOrMsg, msgOrErr, ...args);
	};
}
const logger = {
	...pinoLogger,
	error: wrapLogMethod(pinoLogger.error.bind(pinoLogger)),
	warn: wrapLogMethod(pinoLogger.warn.bind(pinoLogger)),
	info: wrapLogMethod(pinoLogger.info.bind(pinoLogger)),
	debug: wrapLogMethod(pinoLogger.debug.bind(pinoLogger)),
	trace: wrapLogMethod(pinoLogger.trace.bind(pinoLogger)),
	fatal: wrapLogMethod(pinoLogger.fatal.bind(pinoLogger))
};
//#endregion
//#region src/lib/escapeRegExp.ts
/**
* Экранирует спецсимволы строки для безопасного использования в `RegExp`.
*
* @example escapeRegExp("file.txt") // "file\\.txt"
* @example new RegExp(`(${escapeRegExp("a+b")})`, "ig") // ищет буквально "a+b"
*
* @param value - исходная строка
* @returns строка с экранированными символами `. * + ? ^ $ { } ( ) | [ ] \\`
*/
const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
//#endregion
//#region src/lib/copyToClipboard.ts
/**
* Копирует текст в буфер обмена.
*
* Сначала использует `navigator.clipboard.writeText` в secure context;
* при недоступности или ошибке — fallback через `document.execCommand("copy")`.
*
* @example await copyToClipboard("текст для копирования")
*
* @param text - текст для копирования
* @returns promise, который резолвится после успешного копирования
* @throws если оба способа копирования не сработали
*/
const copyToClipboard = async (text) => {
	if (navigator.clipboard && window.isSecureContext) try {
		await navigator.clipboard.writeText(text);
		return;
	} catch (error) {
		console.warn("Modern clipboard API failed, falling back to legacy method", error);
	}
	try {
		const textarea = document.createElement("textarea");
		textarea.value = text;
		textarea.style.position = "fixed";
		textarea.style.opacity = "0";
		document.body.appendChild(textarea);
		textarea.select();
		const success = document.execCommand("copy");
		document.body.removeChild(textarea);
		if (!success) throw new Error("Failed to copy text using legacy method");
	} catch (error) {
		throw new Error(`Failed to copy text: ${error instanceof Error ? error.message : String(error)}`, { cause: error });
	}
};
//#endregion
//#region src/lib/makeStoreProvider.tsx
/**
* Создаёт пару `StoreProvider` и `useStore` для page store класса.
*
* @example
* class PageStore {
*   constructor() {
*     makeAutoObservable(this);
*   }
* }
*
* const { StoreProvider, useStore } = makeStoreProvider(PageStore);
*
* @param PageStore - класс store, инстанс создаётся один раз на провайдер
* @returns провайдер контекста и hook для доступа к store
*/
const makeStoreProvider = (PageStore) => {
	const pageStoreContext = React.createContext(null);
	const useStore = () => {
		const context = React.useContext(pageStoreContext);
		if (context == null) throw new Error("useStore must be used within a StoreProvider");
		return context;
	};
	const makeStore = () => new PageStore();
	const StoreProvider = ({ children }) => {
		const [pageStore] = React.useState(makeStore);
		return /* @__PURE__ */ jsx(pageStoreContext.Provider, {
			value: pageStore,
			children
		});
	};
	return {
		StoreProvider,
		useStore
	};
};
//#endregion
//#region src/lib/arraysEqual.ts
/**
* Сравнивает два массива на поверхностное равенство (`===` по элементам).
*
* @example arraysEqual([1, 2, 3], [1, 2, 3]) // true
* @example arraysEqual(["a"], ["a", "b"]) // false
*
* @param arr1 - первый массив
* @param arr2 - второй массив
* @returns `true`, если массивы одинаковой длины и каждый элемент равен по `===`
*/
const arraysEqual = (arr1, arr2) => {
	if (arr1 === arr2) return true;
	if (arr1.length !== arr2.length) return false;
	for (let i = 0; i < arr1.length; i++) if (arr1[i] !== arr2[i]) return false;
	return true;
};
//#endregion
//#region src/lib/printXlsxBlob.ts
const printXlsxBlob = async (blob, config) => {
	try {
		const arrayBuffer = await blob.arrayBuffer();
		const workbook = XLSX.read(arrayBuffer, { type: "array" });
		const firstSheetName = workbook.SheetNames[0];
		const worksheet = firstSheetName === void 0 ? void 0 : workbook.Sheets[firstSheetName];
		if (worksheet === void 0) throw new Error("XLSX workbook has no sheets");
		const htmlContent = XLSX.utils.sheet_to_html(worksheet, {
			id: "data-table",
			editable: false,
			header: "",
			footer: ""
		});
		const iframe = document.createElement("iframe");
		iframe.style.display = "none";
		iframe.onload = () => {
			setTimeout(() => {
				if (iframe?.contentWindow) {
					iframe.contentWindow.focus();
					iframe.contentWindow.print();
					document.body.removeChild(iframe);
					URL.revokeObjectURL(url);
				}
			}, 500);
		};
		const url = URL.createObjectURL(new Blob([wrapTable(htmlContent)], { type: "text/html" }));
		iframe.src = url;
		document.body.appendChild(iframe);
	} catch (error) {
		console.error("Ошибка при печати XLSX:", error);
		config?.onError?.(error);
	}
};
const wrapTable = (html) => `<!DOCTYPE html>
  <html>
    <head>
      <style>
        table {
          border-collapse: collapse;
          width: 100%;
          overflow-x: hidden;
        }
        th, td {
          border: 1px solid black;
          padding: 4px;
        }
      </style>
    </head>
    <body>
      ${html}
    </body>
  </html>`;
//#endregion
//#region src/lib/qs/formats.ts
/**
* Query string format utilities - from https://github.com/ljharb/qs
*/
const percentTwenties = /%20/g;
const Format = {
	RFC1738: "RFC1738",
	RFC3986: "RFC3986"
};
const formats = {
	default: Format.RFC3986,
	formatters: {
		RFC1738: function(value) {
			return value.replace(percentTwenties, "+");
		},
		RFC3986: function(value) {
			return String(value);
		}
	},
	RFC1738: Format.RFC1738,
	RFC3986: Format.RFC3986
};
//#endregion
//#region src/lib/qs/sideChannel.ts
/**
* Minimal side-channel implementation using WeakMap.
* Inlined from https://github.com/ljharb/side-channel - used by qs for cyclic reference detection.
*/
function getSideChannel() {
	const $channelData = /* @__PURE__ */ new WeakMap();
	return {
		set(key, value) {
			$channelData.set(key, value);
		},
		get(key) {
			return $channelData.get(key);
		},
		has(key) {
			return $channelData.has(key);
		}
	};
}
//#endregion
//#region src/lib/qs/utils.ts
/**
* Query string utils - from https://github.com/ljharb/qs
*/
const has$2 = Object.prototype.hasOwnProperty;
const isArray$2 = Array.isArray;
const overflowChannel = getSideChannel();
function markOverflow(obj, maxIndex) {
	overflowChannel.set(obj, maxIndex);
	return obj;
}
function isOverflow(obj) {
	return overflowChannel.has(obj);
}
function getMaxIndex(obj) {
	return overflowChannel.get(obj);
}
function setMaxIndex(obj, maxIndex) {
	overflowChannel.set(obj, maxIndex);
}
const hexTable = (function() {
	const array = [];
	for (let i = 0; i < 256; ++i) array[array.length] = "%" + ((i < 16 ? "0" : "") + i.toString(16)).toUpperCase();
	return array;
})();
function compactQueue(queue) {
	while (queue.length > 1) {
		const item = queue.pop();
		const obj = item.obj[item.prop];
		if (isArray$2(obj)) {
			const compacted = [];
			for (let j = 0; j < obj.length; ++j) if (typeof obj[j] !== "undefined") compacted[compacted.length] = obj[j];
			item.obj[item.prop] = compacted;
		}
	}
}
function arrayToObject(source, options) {
	const obj = options && options.plainObjects ? Object.create(null) : {};
	for (let i = 0; i < source.length; ++i) if (typeof source[i] !== "undefined") obj[i] = source[i];
	return obj;
}
function merge(target, source, options) {
	if (!source) return target;
	if (typeof source !== "object" && typeof source !== "function") {
		if (isArray$2(target)) {
			const nextIndex = target.length;
			if (options && typeof options.arrayLimit === "number" && nextIndex > options.arrayLimit) return markOverflow(arrayToObject(target.concat(source), options), nextIndex);
			target[nextIndex] = source;
		} else if (target && typeof target === "object") {
			if (isOverflow(target)) {
				const newIndex = getMaxIndex(target) + 1;
				target[newIndex] = source;
				setMaxIndex(target, newIndex);
			} else if (options && options.strictMerge) return [target, source];
			else if (options && (options.plainObjects || options.allowPrototypes) || !has$2.call(Object.prototype, source)) target[source] = true;
		} else return [target, source];
		return target;
	}
	if (!target || typeof target !== "object") {
		if (isOverflow(source)) {
			const sourceKeys = Object.keys(source);
			const result = options && options.plainObjects ? Object.create(null) : { 0: target };
			result[0] = target;
			for (let m = 0; m < sourceKeys.length; m++) {
				const oldKey = parseInt(sourceKeys[m], 10);
				result[oldKey + 1] = source[sourceKeys[m]];
			}
			return markOverflow(result, getMaxIndex(source) + 1);
		}
		const combined = [target].concat(source);
		if (options && typeof options.arrayLimit === "number" && combined.length > options.arrayLimit) return markOverflow(arrayToObject(combined, options), combined.length - 1);
		return combined;
	}
	let mergeTarget = target;
	if (isArray$2(target) && !isArray$2(source)) mergeTarget = arrayToObject(target, options);
	if (isArray$2(target) && isArray$2(source)) {
		source.forEach(function(item, i) {
			if (has$2.call(target, i)) {
				const targetItem = target[i];
				if (targetItem && typeof targetItem === "object" && item && typeof item === "object") target[i] = merge(targetItem, item, options);
				else target.push(item);
			} else target[i] = item;
		});
		return target;
	}
	return Object.keys(source).reduce(function(acc, key) {
		const value = source[key];
		if (has$2.call(acc, key)) acc[key] = merge(acc[key], value, options);
		else acc[key] = value;
		if (isOverflow(source) && !isOverflow(acc)) markOverflow(acc, getMaxIndex(source));
		if (isOverflow(acc)) {
			const keyNum = parseInt(key, 10);
			if (String(keyNum) === key && keyNum >= 0 && keyNum > getMaxIndex(acc)) setMaxIndex(acc, keyNum);
		}
		return acc;
	}, mergeTarget);
}
function decode(str, _defaultDecoder, charset) {
	const strWithoutPlus = str.replace(/\+/g, " ");
	if (charset === "iso-8859-1") return strWithoutPlus.replace(/%[0-9a-f]{2}/gi, unescape);
	try {
		return decodeURIComponent(strWithoutPlus);
	} catch {
		return strWithoutPlus;
	}
}
const limit = 1024;
function encode(str, _defaultEncoder, charset, _kind, format) {
	if (str.length === 0) return str;
	let string = str;
	if (typeof str === "symbol") string = Symbol.prototype.toString.call(str);
	else if (typeof str !== "string") string = String(str);
	if (charset === "iso-8859-1") return escape(string).replace(/%u[0-9a-f]{4}/gi, function($0) {
		return "%26%23" + parseInt($0.slice(2), 16) + "%3B";
	});
	let out = "";
	for (let j = 0; j < string.length; j += limit) {
		const segment = string.length >= limit ? string.slice(j, j + limit) : string;
		const arr = [];
		for (let i = 0; i < segment.length; ++i) {
			let c = segment.charCodeAt(i);
			if (c === 45 || c === 46 || c === 95 || c === 126 || c >= 48 && c <= 57 || c >= 65 && c <= 90 || c >= 97 && c <= 122 || format === formats.RFC1738 && (c === 40 || c === 41)) {
				arr[arr.length] = segment.charAt(i);
				continue;
			}
			if (c < 128) {
				arr[arr.length] = hexTable[c];
				continue;
			}
			if (c < 2048) {
				arr[arr.length] = hexTable[192 | c >> 6] + hexTable[128 | c & 63];
				continue;
			}
			if (c < 55296 || c >= 57344) {
				arr[arr.length] = hexTable[224 | c >> 12] + hexTable[128 | c >> 6 & 63] + hexTable[128 | c & 63];
				continue;
			}
			i += 1;
			c = 65536 + ((c & 1023) << 10 | segment.charCodeAt(i) & 1023);
			arr[arr.length] = hexTable[240 | c >> 18] + hexTable[128 | c >> 12 & 63] + hexTable[128 | c >> 6 & 63] + hexTable[128 | c & 63];
		}
		out += arr.join("");
	}
	return out;
}
function compact(value) {
	const queue = [{
		obj: { o: value },
		prop: "o"
	}];
	const refs = [];
	for (let i = 0; i < queue.length; ++i) {
		const item = queue[i];
		const obj = item.obj[item.prop];
		const keys = Object.keys(obj);
		for (let j = 0; j < keys.length; ++j) {
			const key = keys[j];
			const val = obj[key];
			if (typeof val === "object" && val !== null && refs.indexOf(val) === -1) {
				queue.push({
					obj,
					prop: key
				});
				refs.push(val);
			}
		}
	}
	compactQueue(queue);
	return value;
}
function isRegExp(obj) {
	return Object.prototype.toString.call(obj) === "[object RegExp]";
}
function isBuffer(obj) {
	if (!obj || typeof obj !== "object") return false;
	const o = obj;
	return !!o.constructor?.isBuffer?.(o);
}
function combine(a, b, arrayLimit, plainObjects) {
	if (isOverflow(a)) {
		const newIndex = getMaxIndex(a) + 1;
		a[newIndex] = b;
		setMaxIndex(a, newIndex);
		return a;
	}
	const result = [].concat(a, b);
	if (result.length > arrayLimit) return markOverflow(arrayToObject(result, { plainObjects: !!plainObjects }), result.length - 1);
	return result;
}
function maybeMap(val, fn) {
	if (isArray$2(val)) {
		const mapped = [];
		for (let i = 0; i < val.length; i += 1) mapped[mapped.length] = fn(val[i]);
		return mapped;
	}
	return fn(val);
}
//#endregion
//#region src/lib/qs/parse.ts
/**
* qs parse - Source: https://github.com/ljharb/qs
* License: BSD-3-Clause
*/
const has$1 = Object.prototype.hasOwnProperty;
const isArray$1 = Array.isArray;
const defaults$1 = {
	allowDots: false,
	allowEmptyArrays: false,
	allowPrototypes: false,
	allowSparse: false,
	arrayLimit: 20,
	charset: "utf-8",
	charsetSentinel: false,
	comma: false,
	decodeDotInKeys: false,
	decoder: decode,
	delimiter: "&",
	depth: 5,
	duplicates: "combine",
	ignoreQueryPrefix: false,
	interpretNumericEntities: false,
	parameterLimit: 1e3,
	parseArrays: true,
	plainObjects: false,
	strictDepth: false,
	strictMerge: true,
	strictNullHandling: false,
	throwOnLimitExceeded: false
};
function interpretNumericEntities(str) {
	return str.replace(/&#(\d+);/g, (_$0, numberStr) => {
		return String.fromCharCode(parseInt(numberStr, 10));
	});
}
function parseArrayValue(val, options, currentArrayLength) {
	if (val && typeof val === "string" && options.comma && val.indexOf(",") > -1) return val.split(",");
	if (options.throwOnLimitExceeded && currentArrayLength >= options.arrayLimit) throw new RangeError("Array limit exceeded. Only " + options.arrayLimit + " element" + (options.arrayLimit === 1 ? "" : "s") + " allowed in an array.");
	return val;
}
const isoSentinel = "utf8=%26%2310003%3B";
const charsetSentinel = "utf8=%E2%9C%93";
function parseValues(str, options) {
	const obj = Object.create(null);
	let cleanStr = options.ignoreQueryPrefix ? str.replace(/^\?/, "") : str;
	cleanStr = cleanStr.replace(/%5B/gi, "[").replace(/%5D/gi, "]");
	const limit = options.parameterLimit === Infinity ? void 0 : options.parameterLimit;
	const parts = cleanStr.split(options.delimiter, options.throwOnLimitExceeded ? limit + 1 : limit);
	if (options.throwOnLimitExceeded && parts.length > limit) throw new RangeError("Parameter limit exceeded. Only " + limit + " parameter" + (limit === 1 ? "" : "s") + " allowed.");
	let skipIndex = -1;
	let charset = options.charset;
	if (options.charsetSentinel) {
		for (let i = 0; i < parts.length; ++i) if (parts[i].indexOf("utf8=") === 0) {
			if (parts[i] === charsetSentinel) charset = "utf-8";
			else if (parts[i] === isoSentinel) charset = "iso-8859-1";
			skipIndex = i;
			i = parts.length;
		}
	}
	for (let i = 0; i < parts.length; ++i) {
		if (i === skipIndex) continue;
		const part = parts[i];
		const bracketEqualsPos = part.indexOf("]=");
		const pos = bracketEqualsPos === -1 ? part.indexOf("=") : bracketEqualsPos + 1;
		let key;
		let val;
		if (pos === -1) {
			key = options.decoder(part, defaults$1.decoder, charset, "key");
			val = options.strictNullHandling ? null : "";
		} else {
			key = options.decoder(part.slice(0, pos), defaults$1.decoder, charset, "key");
			if (key !== null) val = maybeMap(parseArrayValue(part.slice(pos + 1), options, isArray$1(obj[key]) ? obj[key].length : 0), (encodedVal) => options.decoder(encodedVal, defaults$1.decoder, charset, "value"));
		}
		if (val && options.interpretNumericEntities && charset === "iso-8859-1") val = interpretNumericEntities(String(val));
		if (part.indexOf("[]=") > -1) val = isArray$1(val) ? [val] : val;
		if (options.comma && isArray$1(val) && val.length > options.arrayLimit) {
			if (options.throwOnLimitExceeded) throw new RangeError("Array limit exceeded. Only " + options.arrayLimit + " element" + (options.arrayLimit === 1 ? "" : "s") + " allowed in an array.");
			val = combine([], val, options.arrayLimit, options.plainObjects);
		}
		if (key !== null) {
			const existing = has$1.call(obj, key);
			if (existing && (options.duplicates === "combine" || part.indexOf("[]=") > -1)) obj[key] = combine(obj[key], val, options.arrayLimit, options.plainObjects);
			else if (!existing || options.duplicates === "last") obj[key] = val;
		}
	}
	return obj;
}
function parseObject(chain, val, options, valuesParsed) {
	let currentArrayLength = 0;
	if (chain.length > 0 && chain[chain.length - 1] === "[]") {
		const parentKey = chain.slice(0, -1).join("");
		const parsedVal = val;
		currentArrayLength = Array.isArray(val) && parsedVal[parentKey] ? parsedVal[parentKey].length : 0;
	}
	let leaf = valuesParsed ? val : parseArrayValue(val, options, currentArrayLength);
	for (let i = chain.length - 1; i >= 0; --i) {
		let obj;
		const root = chain[i];
		if (root === "[]" && options.parseArrays) {
			if (isOverflow(leaf)) obj = leaf;
			else obj = options.allowEmptyArrays && (leaf === "" || options.strictNullHandling && leaf === null) ? [] : combine([], leaf, options.arrayLimit, options.plainObjects);
		} else {
			obj = options.plainObjects ? Object.create(null) : {};
			const cleanRoot = root.charAt(0) === "[" && root.charAt(root.length - 1) === "]" ? root.slice(1, -1) : root;
			const decodedRoot = options.decodeDotInKeys ? cleanRoot.replace(/%2E/g, ".") : cleanRoot;
			const index = parseInt(decodedRoot, 10);
			const isValidArrayIndex = !isNaN(index) && root !== decodedRoot && String(index) === decodedRoot && index >= 0 && options.parseArrays;
			if (!options.parseArrays && decodedRoot === "") obj["0"] = leaf;
			else if (isValidArrayIndex && index < options.arrayLimit) {
				obj = [];
				obj[index] = leaf;
			} else if (isValidArrayIndex && options.throwOnLimitExceeded) throw new RangeError("Array limit exceeded. Only " + options.arrayLimit + " element" + (options.arrayLimit === 1 ? "" : "s") + " allowed in an array.");
			else if (isValidArrayIndex) {
				obj[index] = leaf;
				markOverflow(obj, index);
			} else if (decodedRoot !== "__proto__") obj[decodedRoot] = leaf;
		}
		leaf = obj;
	}
	return leaf;
}
function splitKeyIntoSegments(givenKey, options) {
	const key = options.allowDots ? givenKey.replace(/\.([^.[]+)/g, "[$1]") : givenKey;
	if (options.depth <= 0) {
		if (!options.plainObjects && has$1.call(Object.prototype, key)) {
			if (!options.allowPrototypes) return void 0;
		}
		return [key];
	}
	const brackets = /(\[[^[\]]*])/;
	const child = /(\[[^[\]]*])/g;
	let segment = brackets.exec(key);
	const parent = segment ? key.slice(0, segment.index) : key;
	const keys = [];
	if (parent) {
		if (!options.plainObjects && has$1.call(Object.prototype, parent)) {
			if (!options.allowPrototypes) return void 0;
		}
		keys.push(parent);
	}
	let i = 0;
	while ((segment = child.exec(key)) !== null && i < options.depth) {
		i += 1;
		const segmentContent = segment[1].slice(1, -1);
		if (!options.plainObjects && has$1.call(Object.prototype, segmentContent)) {
			if (!options.allowPrototypes) return void 0;
		}
		keys.push(segment[1]);
	}
	if (segment) {
		if (options.strictDepth === true) throw new RangeError("Input depth exceeded depth option of " + options.depth + " and strictDepth is true");
		keys.push("[" + key.slice(segment.index) + "]");
	}
	return keys;
}
function parseKeys(givenKey, val, options, valuesParsed) {
	if (!givenKey) return void 0;
	const keys = splitKeyIntoSegments(givenKey, options);
	if (!keys) return void 0;
	return parseObject(keys, val, options, valuesParsed);
}
function normalizeParseOptions(opts) {
	if (!opts) return defaults$1;
	if (typeof opts.allowEmptyArrays !== "undefined" && typeof opts.allowEmptyArrays !== "boolean") throw new TypeError("`allowEmptyArrays` option can only be `true` or `false`, when provided");
	if (typeof opts.decodeDotInKeys !== "undefined" && typeof opts.decodeDotInKeys !== "boolean") throw new TypeError("`decodeDotInKeys` option can only be `true` or `false`, when provided");
	if (opts.decoder != null && typeof opts.decoder !== "undefined" && typeof opts.decoder !== "function") throw new TypeError("Decoder has to be a function.");
	if (typeof opts.charset !== "undefined" && opts.charset !== "utf-8" && opts.charset !== "iso-8859-1") throw new TypeError("The charset option must be either utf-8, iso-8859-1, or undefined");
	if (typeof opts.throwOnLimitExceeded !== "undefined" && typeof opts.throwOnLimitExceeded !== "boolean") throw new TypeError("`throwOnLimitExceeded` option must be a boolean");
	const charset = typeof opts.charset === "undefined" ? defaults$1.charset : opts.charset;
	const duplicates = typeof opts.duplicates === "undefined" ? defaults$1.duplicates : opts.duplicates;
	if (duplicates !== "combine" && duplicates !== "first" && duplicates !== "last") throw new TypeError("The duplicates option must be either combine, first, or last");
	return {
		allowDots: typeof opts.allowDots === "undefined" ? opts.decodeDotInKeys === true ? true : defaults$1.allowDots : !!opts.allowDots,
		allowEmptyArrays: typeof opts.allowEmptyArrays === "boolean" ? !!opts.allowEmptyArrays : defaults$1.allowEmptyArrays,
		allowPrototypes: typeof opts.allowPrototypes === "boolean" ? opts.allowPrototypes : defaults$1.allowPrototypes,
		allowSparse: typeof opts.allowSparse === "boolean" ? opts.allowSparse : defaults$1.allowSparse,
		arrayLimit: typeof opts.arrayLimit === "number" ? opts.arrayLimit : defaults$1.arrayLimit,
		charset,
		charsetSentinel: typeof opts.charsetSentinel === "boolean" ? opts.charsetSentinel : defaults$1.charsetSentinel,
		comma: typeof opts.comma === "boolean" ? opts.comma : defaults$1.comma,
		decodeDotInKeys: typeof opts.decodeDotInKeys === "boolean" ? opts.decodeDotInKeys : defaults$1.decodeDotInKeys,
		decoder: typeof opts.decoder === "function" ? opts.decoder : defaults$1.decoder,
		delimiter: typeof opts.delimiter === "string" || isRegExp(opts.delimiter) ? opts.delimiter : defaults$1.delimiter,
		depth: typeof opts.depth === "number" || opts.depth === false ? +opts.depth : defaults$1.depth,
		duplicates,
		ignoreQueryPrefix: opts.ignoreQueryPrefix === true,
		interpretNumericEntities: typeof opts.interpretNumericEntities === "boolean" ? opts.interpretNumericEntities : defaults$1.interpretNumericEntities,
		parameterLimit: typeof opts.parameterLimit === "number" ? opts.parameterLimit : defaults$1.parameterLimit,
		parseArrays: opts.parseArrays !== false,
		plainObjects: typeof opts.plainObjects === "boolean" ? opts.plainObjects : defaults$1.plainObjects,
		strictDepth: typeof opts.strictDepth === "boolean" ? !!opts.strictDepth : defaults$1.strictDepth,
		strictMerge: typeof opts.strictMerge === "boolean" ? !!opts.strictMerge : defaults$1.strictMerge,
		strictNullHandling: typeof opts.strictNullHandling === "boolean" ? opts.strictNullHandling : defaults$1.strictNullHandling,
		throwOnLimitExceeded: typeof opts.throwOnLimitExceeded === "boolean" ? opts.throwOnLimitExceeded : false
	};
}
function parse(str, opts) {
	const options = normalizeParseOptions(opts);
	if (str === "" || str === null || typeof str === "undefined") return options.plainObjects ? Object.create(null) : {};
	const tempObj = typeof str === "string" ? parseValues(str, options) : str;
	let obj = options.plainObjects ? Object.create(null) : {};
	const keys = Object.keys(tempObj);
	for (let i = 0; i < keys.length; ++i) {
		const key = keys[i];
		const newObj = parseKeys(key, tempObj[key], options, typeof str === "string");
		obj = merge(obj, newObj, options);
	}
	if (options.allowSparse === true) return obj;
	return compact(obj);
}
//#endregion
//#region src/lib/qs/stringify.ts
/**
* qs stringify - Source: https://github.com/ljharb/qs
* License: BSD-3-Clause
*/
const has = Object.prototype.hasOwnProperty;
const isArray = Array.isArray;
const push = Array.prototype.push;
function pushToArray(arr, valueOrArray) {
	push.apply(arr, isArray(valueOrArray) ? valueOrArray : [valueOrArray]);
}
const toISO = Date.prototype.toISOString;
const arrayPrefixGenerators = {
	brackets: (prefix) => prefix + "[]",
	comma: "comma",
	indices: (prefix, key) => prefix + "[" + key + "]",
	repeat: (prefix) => prefix
};
const defaultFormat = formats.default;
const defaults = {
	addQueryPrefix: false,
	allowDots: false,
	allowEmptyArrays: false,
	arrayFormat: "indices",
	charset: "utf-8",
	charsetSentinel: false,
	commaRoundTrip: false,
	delimiter: "&",
	encode: true,
	encodeDotInKeys: false,
	encoder: encode,
	encodeValuesOnly: false,
	filter: void 0,
	format: defaultFormat,
	formatter: formats.formatters[defaultFormat],
	indices: false,
	serializeDate: (date) => toISO.call(date),
	skipNulls: false,
	strictNullHandling: false
};
function isNonNullishPrimitive(v) {
	return typeof v === "string" || typeof v === "number" || typeof v === "boolean" || typeof v === "symbol" || typeof v === "bigint";
}
const sentinel = {};
function stringifyRecurse(object, prefix, generateArrayPrefix, commaRoundTrip, allowEmptyArrays, strictNullHandling, skipNulls, encodeDotInKeys, encoder, filter, sort, allowDots, serializeDate, format, formatter, encodeValuesOnly, charset, sideChannel) {
	let obj = object;
	let tmpSc = sideChannel;
	let step = 0;
	let findFlag = false;
	while ((tmpSc = tmpSc.get(sentinel)) !== void 0 && !findFlag) {
		const pos = tmpSc.get(obj);
		step += 1;
		if (typeof pos !== "undefined") {
			if (pos === step) throw new RangeError("Cyclic object value");
			else findFlag = true;
		}
		if (typeof tmpSc.get(sentinel) === "undefined") step = 0;
	}
	if (typeof filter === "function") obj = filter(prefix, obj);
	else if (obj instanceof Date) obj = serializeDate(obj);
	else if (generateArrayPrefix === "comma" && isArray(obj)) obj = maybeMap(obj, (value) => {
		if (value instanceof Date) return serializeDate(value);
		return value;
	});
	if (obj === null) {
		if (strictNullHandling) return encoder && !encodeValuesOnly ? [encoder(prefix, defaults.encoder, charset, "key", format)] : [prefix];
		obj = "";
	}
	if (isNonNullishPrimitive(obj) || isBuffer(obj)) {
		if (encoder) {
			const keyValue = encodeValuesOnly ? prefix : encoder(prefix, defaults.encoder, charset, "key", format);
			return [formatter(String(keyValue)) + "=" + formatter(encoder(obj, defaults.encoder, charset, "value", format))];
		}
		return [formatter(String(prefix)) + "=" + formatter(String(obj))];
	}
	const values = [];
	if (typeof obj === "undefined") return values;
	let objKeys;
	if (generateArrayPrefix === "comma" && isArray(obj)) {
		if (encodeValuesOnly && encoder) obj = maybeMap(obj, encoder);
		objKeys = [{ value: obj.length > 0 ? obj.join(",") ?? void 0 : void 0 }];
	} else if (isArray(filter)) objKeys = filter;
	else {
		const keys = Object.keys(obj);
		objKeys = sort ? keys.toSorted(sort) : keys;
	}
	const encodedPrefix = encodeDotInKeys ? String(prefix).replace(/\./g, "%2E") : String(prefix);
	const adjustedPrefix = commaRoundTrip && isArray(obj) && obj.length === 1 ? encodedPrefix + "[]" : encodedPrefix;
	if (allowEmptyArrays && isArray(obj) && obj.length === 0) return [adjustedPrefix + "[]"];
	for (let j = 0; j < objKeys.length; ++j) {
		const key = objKeys[j];
		const value = typeof key === "object" && key && typeof key.value !== "undefined" ? key.value : obj[key];
		if (skipNulls && value === null) continue;
		const encodedKey = allowDots && encodeDotInKeys ? String(key).replace(/\./g, "%2E") : String(key);
		const keyPrefix = isArray(obj) ? typeof generateArrayPrefix === "function" ? generateArrayPrefix(adjustedPrefix, encodedKey) : adjustedPrefix : adjustedPrefix + (allowDots ? "." + encodedKey : "[" + encodedKey + "]");
		sideChannel.set(obj, step);
		const valueSideChannel = getSideChannel();
		valueSideChannel.set(sentinel, sideChannel);
		pushToArray(values, stringifyRecurse(value, keyPrefix, generateArrayPrefix, commaRoundTrip, allowEmptyArrays, strictNullHandling, skipNulls, encodeDotInKeys, generateArrayPrefix === "comma" && encodeValuesOnly && isArray(obj) ? null : encoder, filter, sort, allowDots, serializeDate, format, formatter, encodeValuesOnly, charset, valueSideChannel));
	}
	return values;
}
function normalizeStringifyOptions(opts) {
	if (!opts) return defaults;
	if (typeof opts.allowEmptyArrays !== "undefined" && typeof opts.allowEmptyArrays !== "boolean") throw new TypeError("`allowEmptyArrays` option can only be `true` or `false`, when provided");
	if (typeof opts.encodeDotInKeys !== "undefined" && typeof opts.encodeDotInKeys !== "boolean") throw new TypeError("`encodeDotInKeys` option can only be `true` or `false`, when provided");
	if (opts.encoder != null && typeof opts.encoder !== "undefined" && typeof opts.encoder !== "function") throw new TypeError("Encoder has to be a function.");
	const charset = opts.charset || defaults.charset;
	if (typeof opts.charset !== "undefined" && opts.charset !== "utf-8" && opts.charset !== "iso-8859-1") throw new TypeError("The charset option must be either utf-8, iso-8859-1, or undefined");
	let format = formats.default;
	if (typeof opts.format !== "undefined") {
		if (!has.call(formats.formatters, opts.format)) throw new TypeError("Unknown format option provided.");
		format = opts.format;
	}
	const formatter = formats.formatters[format];
	let filter = defaults.filter;
	if (typeof opts.filter === "function" || isArray(opts.filter)) filter = opts.filter;
	let arrayFormat;
	if (opts.arrayFormat && opts.arrayFormat in arrayPrefixGenerators) arrayFormat = opts.arrayFormat;
	else if ("indices" in opts && opts.indices !== void 0) arrayFormat = opts.indices ? "indices" : "repeat";
	else arrayFormat = defaults.arrayFormat;
	if ("commaRoundTrip" in opts && typeof opts.commaRoundTrip !== "boolean") throw new TypeError("`commaRoundTrip` must be a boolean, or absent");
	const allowDots = typeof opts.allowDots === "undefined" ? opts.encodeDotInKeys === true ? true : defaults.allowDots : !!opts.allowDots;
	return {
		...defaults,
		addQueryPrefix: typeof opts.addQueryPrefix === "boolean" ? opts.addQueryPrefix : defaults.addQueryPrefix,
		allowDots,
		allowEmptyArrays: typeof opts.allowEmptyArrays === "boolean" ? !!opts.allowEmptyArrays : defaults.allowEmptyArrays,
		arrayFormat,
		charset,
		charsetSentinel: typeof opts.charsetSentinel === "boolean" ? opts.charsetSentinel : defaults.charsetSentinel,
		commaRoundTrip: !!opts.commaRoundTrip,
		delimiter: typeof opts.delimiter === "undefined" ? defaults.delimiter : opts.delimiter,
		encode: typeof opts.encode === "boolean" ? opts.encode : defaults.encode,
		encodeDotInKeys: typeof opts.encodeDotInKeys === "boolean" ? opts.encodeDotInKeys : defaults.encodeDotInKeys,
		encoder: typeof opts.encoder === "function" ? opts.encoder : defaults.encoder,
		encodeValuesOnly: typeof opts.encodeValuesOnly === "boolean" ? opts.encodeValuesOnly : defaults.encodeValuesOnly,
		filter,
		format,
		formatter,
		serializeDate: typeof opts.serializeDate === "function" ? opts.serializeDate : defaults.serializeDate,
		skipNulls: typeof opts.skipNulls === "boolean" ? opts.skipNulls : defaults.skipNulls,
		sort: typeof opts.sort === "function" ? opts.sort : null,
		strictNullHandling: typeof opts.strictNullHandling === "boolean" ? opts.strictNullHandling : defaults.strictNullHandling
	};
}
function stringify(object, opts) {
	let obj = object;
	const options = normalizeStringifyOptions(opts);
	if (typeof obj !== "object" || obj === null) return "";
	let objKeys;
	let filter;
	if (typeof options.filter === "function") {
		filter = options.filter;
		obj = filter("", obj);
		objKeys = void 0;
	} else if (isArray(options.filter)) {
		filter = options.filter;
		objKeys = filter;
	} else objKeys = Object.keys(obj);
	const keys = [];
	if (typeof obj !== "object" || obj === null) return "";
	const generateArrayPrefix = arrayPrefixGenerators[options.arrayFormat] || arrayPrefixGenerators["indices"];
	const commaRoundTrip = generateArrayPrefix === "comma" && options.commaRoundTrip;
	if (objKeys === void 0) objKeys = Object.keys(obj);
	if (options.sort) objKeys.sort(options.sort);
	const sideChannel = getSideChannel();
	for (let i = 0; i < objKeys.length; ++i) {
		const key = objKeys[i];
		const value = obj[key];
		if (options.skipNulls && value === null) continue;
		pushToArray(keys, stringifyRecurse(value, key, generateArrayPrefix, commaRoundTrip, options.allowEmptyArrays, options.strictNullHandling, options.skipNulls, options.encodeDotInKeys, options.encode ? options.encoder : null, options.filter, options.sort, options.allowDots, options.serializeDate, options.format, options.formatter, options.encodeValuesOnly, options.charset, sideChannel));
	}
	const joined = keys.join(options.delimiter);
	let prefix = options.addQueryPrefix === true ? "?" : "";
	if (options.charsetSentinel) {
		if (options.charset === "iso-8859-1") prefix += "utf8=%26%2310003%3B&";
		else prefix += "utf8=%E2%9C%93&";
	}
	return joined.length > 0 ? prefix + joined : "";
}
//#endregion
//#region src/lib/cn.ts
/**
* Combines clsx and tailwind-merge for optimal class merging.
* @param inputs - Class names or conditional class values.
* @returns A single string with merged class names.
*/
const cn = (...inputs) => {
	return twMerge(clsx(inputs));
};
//#endregion
//#region src/lib/mergeRefs.ts
/**
* Объединяет несколько React ref в один callback ref.
*
* @example
* const ref = useRef<HTMLDivElement>(null);
* <div ref={mergeRefs(ref, forwardedRef)} />
*
* @param refs - callback ref, object ref или undefined
* @returns callback ref, который прокидывает значение во все переданные ref
*/
function mergeRefs(...refs) {
	return (value) => {
		refs.forEach((r) => {
			if (typeof r === "function") r(value);
			else if (r != null) r.current = value;
		});
	};
}
//#endregion
//#region src/lib/naturalSort.ts
const CHAR_PRIORITIES = {
	"-": 65,
	".": 45,
	"/": 46
};
const CHAR_CODE_RANGES = [
	{
		min: 48,
		max: 57,
		transform: (code) => code + 18
	},
	{
		min: 65,
		max: 90,
		transform: (code) => code + 11
	},
	{
		min: 97,
		max: 122,
		transform: (code) => code + 5
	},
	{
		min: 58,
		max: 64,
		transform: (code) => code - 11
	},
	{
		min: 91,
		max: 96,
		transform: (code) => code - 37
	},
	{
		min: 123,
		max: 127,
		transform: (code) => code - 63
	}
];
const LETTERS = /* @__PURE__ */ new Set([...Array.from({ length: 26 }, (_, i) => String.fromCharCode(i + 97)), ...Array.from({ length: 26 }, (_, i) => String.fromCharCode(i + 65))]);
/**
* Получает код символа для сортировки
*/
const getCharCode = (char) => {
	if (char == null || char === "") return 0;
	if (CHAR_PRIORITIES[char] !== void 0) return CHAR_PRIORITIES[char];
	if (LETTERS.has(char)) return char.toLowerCase().charCodeAt(0) - 97 + 76;
	const charCode = char.charCodeAt(0);
	if (charCode < 45 || charCode > 127) return charCode;
	for (const range of CHAR_CODE_RANGES) if (charCode >= range.min && charCode <= range.max) return range.transform(charCode);
	return charCode;
};
const isDigit = (char) => {
	if (char == null || char === "") return false;
	const charCode = char.charCodeAt(0);
	return charCode >= 48 && charCode <= 57;
};
/**
* Извлекает число из строки начиная с указанной позиции
*/
const extractNumber = (str, startPos) => {
	let endPos = startPos;
	let numberStr = "";
	let hasDecimal = false;
	while (endPos < str.length) {
		const char = str[endPos];
		if (isDigit(char)) {
			numberStr += char;
			endPos++;
		} else if (char === "," && !hasDecimal) {
			if (isDigit(str[endPos + 1])) {
				numberStr += ".";
				hasDecimal = true;
				endPos++;
			} else break;
		} else break;
	}
	return {
		value: numberStr ? parseFloat(numberStr) : 0,
		endPos
	};
};
const sortKey = (value) => String(value ?? "").toLowerCase();
/**
* Естественная сортировка строк с учетом чисел
*/
const naturalSort = (a, b) => {
	const strA = sortKey(a);
	const strB = sortKey(b);
	if (strA === strB) return String(a ?? "").localeCompare(String(b ?? ""));
	let posA = 0;
	let posB = 0;
	while (posA < strA.length && posB < strB.length) {
		const charA = strA[posA];
		const charB = strB[posB];
		let codeA = getCharCode(charA);
		let codeB = getCharCode(charB);
		if (codeA >= 66 && codeA <= 75 && codeB >= 66 && codeB <= 75) {
			const numA = extractNumber(strA, posA);
			const numB = extractNumber(strB, posB);
			codeA = numA.value;
			codeB = numB.value;
			posA = numA.endPos;
			posB = numB.endPos;
		} else {
			posA++;
			posB++;
		}
		if (codeA !== codeB) return codeA < codeB ? -1 : 1;
	}
	if (strA.length !== strB.length) return strA.length - strB.length;
	return String(a ?? "").localeCompare(String(b ?? ""));
};
//#endregion
//#region src/lib/hooks/useDownload.ts
const useDownload = () => {
	const download = (fileMeta, blobPart) => {
		const blob = new File([blobPart], fileMeta.baseName);
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement("a");
		const fileName = fileMeta.ext ? `${fileMeta.baseName}.${fileMeta.ext}` : fileMeta.baseName;
		link.href = url;
		link.setAttribute("download", fileName);
		document.body.appendChild(link);
		link.click();
		link.remove();
	};
	return download;
};
//#endregion
//#region src/lib/hooks/useLocalStorage.ts
function getStorageValue(key, defaultValue) {
	const saved = localStorage.getItem(key);
	return saved ? JSON.parse(saved) : defaultValue;
}
const useLocalStorage = (key, defaultValue) => {
	const [value, setValue] = useState(() => {
		return getStorageValue(key, defaultValue);
	});
	useEffect(() => {
		localStorage.setItem(key, JSON.stringify(value));
	}, [key, value]);
	return [value, setValue];
};
//#endregion
//#region src/lib/hooks/usePrint.ts
const usePrint = () => {
	const print = (fileMeta, blobPart, mime = "application/pdf") => {
		const blob = new Blob([blobPart], { type: mime });
		const blobURL = URL.createObjectURL(blob);
		const previousIframe = document.getElementById(`print/${fileMeta.id}`);
		if (previousIframe) previousIframe.remove();
		const iframe = document.createElement("iframe");
		document.body.appendChild(iframe);
		iframe.id = `print/${fileMeta.id}`;
		iframe.style.display = "none";
		iframe.src = blobURL;
		iframe.onload = () => {
			setTimeout(() => {
				iframe.focus();
				iframe.contentWindow?.print();
			}, 0);
		};
	};
	return print;
};
//#endregion
//#region src/lib/hooks/useFileSelector.ts
/**
* @pram props.fileExtensions - массив расширений. Точку перед расширением не добавлять!
*/
const useFileSelector = ({ setFile, setFiles, fileExtensions, multiple = false }) => {
	const inputRef = React.useRef(void 0);
	React.useEffect(() => {
		return () => {
			if (inputRef.current) inputRef.current.remove();
		};
	}, []);
	return { onSelectFile: React.useCallback((context) => {
		if (!inputRef.current) {
			inputRef.current = document.createElement("input");
			inputRef.current.type = "file";
			inputRef.current.style.display = "none";
			if (fileExtensions?.length) inputRef.current.accept = fileExtensions.filter(Boolean).map((ext) => ext.startsWith(".") ? ext : `.${ext}`).join(", ");
		}
		inputRef.current.multiple = multiple;
		if (!document.body.contains(inputRef.current)) document.body.appendChild(inputRef.current);
		inputRef.current.value = "";
		inputRef.current.onchange = () => {
			const selectedFiles = inputRef.current?.files ? Array.from(inputRef.current.files) : [];
			if (selectedFiles.length === 0) {
				setFile?.(null, context);
				return;
			}
			if (multiple && setFiles) {
				setFiles(selectedFiles, context);
				return;
			}
			setFile?.(selectedFiles[0] ?? null, context);
		};
		inputRef.current.onclose = () => {
			setFile?.(null, context);
		};
		inputRef.current.click();
	}, [
		setFile,
		setFiles,
		fileExtensions,
		multiple
	]) };
};
//#endregion
//#region src/lib/hooks/useSearch.ts
const parser = parseAsString.withOptions({ clearOnDefault: true });
const useSearch = () => useQueryState("search", parser);
//#endregion
//#region src/lib/hooks/useColumnSort.ts
const directionParser = parseAsStringEnum(["ASC", "DESC"]).withDefault("ASC").withOptions({ clearOnDefault: true });
const sortFieldParser = parseAsString.withOptions({ clearOnDefault: true });
const useColumnSort = () => useQueryStates({
	direction: directionParser,
	sortField: sortFieldParser
});
//#endregion
export { Format, GLTFLoader, THREE, arraySplitter, arraysEqual, cn, convertBlobToBase64, copyToClipboard, defaultMapboxStyle, ensureUuidV4Polyfill, escapeRegExp, formatBytes, formats, getBrowser, getNoun, groupBy, isColorTooBlack, lazyLoad, localeDate, logger, makeStoreProvider, mergeRefs, naturalSort, parse, printXlsxBlob, requestSplitter, sliceIntoChunks, stringify, useColumnSort, useDownload, useFileSelector, useLocalStorage, usePrint, useSearch, uuidv4 };

//# sourceMappingURL=index.js.map